import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import { Modal, Button } from 'react-bootstrap';
import { Rnd } from 'react-rnd';
import EditRutaPanel from './shared/EditRutaPanel';
import ToggleSwitch from './shared/ToggleSwitch';
import OperadorOTsModal from './shared/OperadorOTsModal';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon in Leaflet when using Webpack/CRA
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const claveToImporteField = {
    'A': 'Automovil',
    'B': 'Autobus2Ejes',
    'C-2': 'Camion2Ejes',
    'C-3': 'Camion3Ejes',
    'C-4': 'Camion3Ejes',
    'C-5': 'Camion5Ejes',
    'C-9': 'Camion9Ejes'
};

const formatDatabaseDate = (value, options = {}) => {
    if (!value) return '-';

    return new Date(value).toLocaleString('es-MX', {
        timeZone: 'America/Mexico_City',
        ...options
    });
};

const ResizeAwareMap = () => {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const triggerInvalidate = () => {
            requestAnimationFrame(() => {
                map.invalidateSize({ pan: false });
            });
        };

        triggerInvalidate();

        const handleResize = () => triggerInvalidate();
        window.addEventListener('resize', handleResize);

        let observer;
        const container = map.getContainer();
        if (typeof ResizeObserver !== 'undefined' && container) {
            observer = new ResizeObserver(() => triggerInvalidate());
            observer.observe(container);
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            if (observer) observer.disconnect();
        };
    }, [map]);

    return null;
};

const MapViewport = ({ latitude, longitude }) => {
    const map = useMap();

    useEffect(() => {
        if (latitude == null || longitude == null) return;
        map.setView([parseFloat(latitude), parseFloat(longitude)], map.getZoom(), { animate: true });
    }, [latitude, longitude, map]);

    return null;
};

const TrackingModule = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // State
    const [matriculasText, setMatriculasText] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [escala, setEscala] = useState(8000); // Default scale
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    // Lista de Operadores
    const [operadores, setOperadores] = useState([]);
    const [selectedOpDropdown, setSelectedOpDropdown] = useState("");

    // Map Modal State
    const [openMapWindows, setOpenMapWindows] = useState([]);
    const [refreshingLocations, setRefreshingLocations] = useState({});
    const [fullscreenMap, setFullscreenMap] = useState(null);
    const mapWindowRefs = useRef({});
    const [selectedRouteFilter, setSelectedRouteFilter] = useState(null);

    // Operator OTs List Modal State
    const [otsModal, setOtsModal] = useState({ show: false, matricula: '', nombre: '' });

    // Directory Detail Modal State
    const [showDirModal, setShowDirModal] = useState(false);
    const [selectedDirEntry, setSelectedDirEntry] = useState(null);

    // Operator Profile Modal State
    const [showOperadorModal, setShowOperadorModal] = useState(false);
    const [operadorInfoAdicional, setOperadorInfoAdicional] = useState(true);
    const [operadorModal, setOperadorModal] = useState({ matricula: '', nombre: '', perfil: null, loading: false, error: '' });
    const [operadorTagsModal, setOperadorTagsModal] = useState({ show: false, tags: [], loading: false, error: '' });
    const [tagToggleConfirm, setTagToggleConfirm] = useState(null); // { tag, accion, saving }

    // Casetas Modal State
    const [showCasetasModal, setShowCasetasModal] = useState(false);
    const [casetasData, setCasetasData] = useState({ expected: [], crossed: [], tagCruces: [], loading: false, ot: '', id_tipo_ruta: '', id_clave: '', id_usuario: '', tag: '', error: '' });
    const [manualLinks, setManualLinks] = useState({});
    const [savingLinks, setSavingLinks] = useState(false);

    // TAG Cruces Modal State
    const [showTagModal, setShowTagModal] = useState(false);
    const [tagModalData, setTagModalData] = useState({ tag: '', cruces: [], loading: false, error: '' });
    const [tagOTInputs, setTagOTInputs] = useState({});   // { [cruceId]: string } — valor del input de OT por cruce
    const [tagSavingOT, setTagSavingOT] = useState(new Set()); // IDs de cruces en proceso de guardado

    // Edit Ruta Panel State
    const [showEditRutaPanel, setShowEditRutaPanel] = useState(false);

    // Table Filters State
    const [tableFilters, setTableFilters] = useState({ matricula: '', ot: '', municipio: '', progreso: '', estadoPersonal: '', ot_prev: '', ot_next: '' });

    // API URL from environment or default
    const API_URL = process.env.REACT_APP_API_URL || "http://192.168.1.252:3001";

    useEffect(() => {
        // Cargar lista de operadores al montar el componente
        const fetchOperadores = async () => {
            try {
                const response = await apiClient.get(`/api/tracking/operadores`);
                setOperadores(response.data);
            } catch (err) {
                console.error("Error cargando operadores:", err);
            }
        };
        fetchOperadores();

        // Si la URL contiene ?ot=, abrir el modal de casetas para esa OT
        const otFromUrl = new URLSearchParams(window.location.search).get('ot');
        if (otFromUrl) {
            handleOpenCasetasByOT(otFromUrl);
        }

        // Si la URL contiene ?matriculas=, pre-cargar el textarea y lanzar rastreo
        const matriculasFromUrl = new URLSearchParams(window.location.search).get('matriculas');
        if (matriculasFromUrl) {
            const texto = matriculasFromUrl.split(',').join('\n');
            setMatriculasText(texto);
            // Lanzar búsqueda con las matrículas de la URL después de establecer el estado
            setTimeout(() => handleTrackWithMatriculas(matriculasFromUrl.split(',').map(m => m.trim()).filter(Boolean)), 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [API_URL]);

    const handleSelectOperatorChange = (e) => {
        const matricula = e.target.value;
        if (matricula) {
            setMatriculasText(prev => prev ? `${prev}\n${matricula}` : `${matricula}`);
            // Resetear el select visualmente
            setSelectedOpDropdown("");
        }
    };

    const handleFilterGPSInactive = () => {
        const gpsInactivos = results.filter(r => !r.latitud || !r.longitud);
        if (gpsInactivos.length === 0) {
            alert("No hay unidades con GPS inactivo.");
            return;
        }
        const gpsInactivosIds = gpsInactivos.map(r => r.id).join('\n');
        alert(`Unidades con GPS inactivo:\n${gpsInactivosIds}`);
    };



    const handleAddAllOperators = () => {
        if (operadores.length > 0) {
            const todasLasMatriculas = operadores.map(op => op.id).join('\n');
            setMatriculasText(prev => prev ? `${prev}\n${todasLasMatriculas}` : todasLasMatriculas);
        }
    };

    const handleOpenDirModal = (detalle, tipo) => {
        setSelectedDirEntry({ ...detalle, tipo });
        setShowDirModal(true);
    };

    const handleCopyOT = (ot) => {
        if (!ot) return;
        navigator.clipboard.writeText(ot).catch(err => console.error('Error al copiar la OT:', err));
    };

    const handleOpenOperadorModal = async (matricula, nombre) => {
        setShowOperadorModal(true);
        setOperadorModal({ matricula, nombre, perfil: null, loading: true, error: '' });
        try {
            const res = await apiClient.get(`/api/tracking/operadores/${matricula}/perfil`);
            setOperadorModal({ matricula, nombre, perfil: res.data, loading: false, error: '' });
        } catch (err) {
            setOperadorModal({ matricula, nombre, perfil: null, loading: false, error: 'No se pudo cargar la información del operador.' });
        }
    };

    const handleOpenOperadorTagsModal = async () => {
        setOperadorTagsModal({ show: true, tags: [], loading: true, error: '' });
        try {
            const res = await apiClient.get(`/api/tracking/operadores/${operadorModal.matricula}/tags`);
            setOperadorTagsModal({ show: true, tags: res.data, loading: false, error: '' });
        } catch (err) {
            setOperadorTagsModal({ show: true, tags: [], loading: false, error: 'No se pudo cargar el historial de TAGs del operador.' });
        }
    };

    const handleToggleTagClick = (tag) => {
        const accion = tag.activo ? 'DESACTIVAR' : 'REACTIVAR';
        window.open(`https://apps.pase.com.mx/uc/detalletag/IMDM/${tag?.tag?.replace('IMDM', '')}`, '_blank', 'noreferrer');
        setTagToggleConfirm({ tag: tag.tag, accion, saving: false });
    };

    const cancelTagToggle = () => setTagToggleConfirm(null);

    const confirmTagToggle = async () => {
        if (!tagToggleConfirm) return;
        setTagToggleConfirm((prev) => ({ ...prev, saving: true }));
        try {
            await apiClient.put(`/api/tags/update-status`, { tag: tagToggleConfirm.tag, accion: tagToggleConfirm.accion });
            const res = await apiClient.get(`/api/tracking/operadores/${operadorModal.matricula}/tags`);
            setOperadorTagsModal((prev) => ({ ...prev, tags: res.data }));
            setTagToggleConfirm(null);
        } catch (err) {
            console.error('Error al actualizar estatus del TAG:', err);
            alert('Hubo un error al intentar actualizar el estado del TAG.');
            setTagToggleConfirm(null);
        }
    };

    const handleOpenTagModal = async (tag) => {
        setShowTagModal(true);
        setTagModalData({ tag, cruces: [], loading: true, error: '' });
        try {
            const res = await apiClient.get(`/api/cruces/tag/${tag}?dias=90`);
            setTagModalData({ tag, cruces: res.data || [], loading: false, error: '' });
        } catch (err) {
            setTagModalData({ tag, cruces: [], loading: false, error: 'Error al cargar los registros del TAG' });
        }
    };

    const handleOpenCasetasByOT = async (otId) => {
        if (!otId) return;
        setShowCasetasModal(true);
        setCasetasData({ expected: [], crossed: [], loading: true, ot: otId, id_tipo_ruta: '', id_clave: '', id_usuario: '', tag: '', error: '', otDetails: null });
        try {
            const resOT = await apiClient.get(`/api/tracking/ot/${otId}`);
            const { info } = resOT.data;
            if (!info) {
                setCasetasData(prev => ({ ...prev, loading: false, error: `La OT "${otId}" no fue encontrada.` }));
                return;
            }
            if (!info.Id_tipo_ruta) {
                setCasetasData(prev => ({ ...prev, loading: false, error: `La OT "${otId}" no tiene una ruta asignada.` }));
                return;
            }
            handleOpenCasetas(otId, info.Id_tipo_ruta, info.Tag || null, info.ID_Clave || null, info.ID_usuario || null);
        } catch (err) {
            const msg = err.response?.status === 404
                ? `La OT "${otId}" no fue encontrada.`
                : 'Error al buscar la OT en el servidor.';
            setCasetasData(prev => ({ ...prev, loading: false, error: msg }));
        }
    };

    const handleOpenCasetas = async (otId, id_tipo_ruta, tag, id_clave, id_usuario) => {
        if (!otId || otId === 'Sin Asignar' || !id_tipo_ruta) return;
        setShowCasetasModal(true);
        setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('ot', otId); return next; });
        setCasetasData({ expected: [], crossed: [], loading: true, ot: otId, id_tipo_ruta: id_tipo_ruta, id_clave: id_clave, id_usuario: id_usuario, tag: tag, error: '', otDetails: null });

        try {
            // 1. Primero obtener detalles de la OT para conocer el rango de fechas
            let otDetails = null;
            try {
                const resTracking = await apiClient.get(`/api/tracking/ot/${otId}`);
                otDetails = resTracking.data;
            } catch (err) {
                console.error('Error fetching OT tracking details:', err);
            }

            // 2. Obtener casetas y cruces en paralelo
            // No filtramos por fecha: si un cruce ya tiene id_orden asignado,
            // se muestra siempre, independientemente de la hora del cruce.
            const [resCasetas, resCruces] = await Promise.all([
                apiClient.get(`/api/casetas/rutas/${id_tipo_ruta}/casetasPorRuta`),
                apiClient.get(`/api/cruces/ot/${otId}`)
            ]);

            // 4. Si hay un TAG disponible, obtener sus cruces con ventana extendida
            //    para detectar cruces fuera del rango de la OT (ej: operador cerró antes de tiempo)
            const resolvedTag = tag || otDetails?.info?.Tag || resCruces.data?.find(c => c.Tag)?.Tag;

            setCasetasData({
                expected: resCasetas.data || [],
                crossed: resCruces.data || [],
                tagCruces: [],
                otDetails,
                loading: false,
                ot: otId,
                id_tipo_ruta: id_tipo_ruta,
                id_clave: id_clave,
                id_usuario: id_usuario,
                tag: resolvedTag || tag,
                error: ''
            });
            if (resolvedTag) {
                try {
                    const tagRes = await apiClient.get(`/api/cruces/tag/${resolvedTag}?dias=20`);
                    setCasetasData(prev => ({ ...prev, tagCruces: tagRes.data || [] }));
                } catch (e) {
                    console.warn('No se pudo cargar cruces del TAG para ventana extendida:', e);
                }
            }
        } catch (error) {
            console.error('Error fetching casetas/cruces:', error);
            setCasetasData(prev => ({ ...prev, loading: false, error: 'Error al cargar los datos de casetas' }));
        }
    };

    const handleOpenEditRuta = () => {
        setShowEditRutaPanel(true);
    };

    const handleOpenMap = (operator) => {
        if (operator && operator.latitud && operator.longitud) {
            setOpenMapWindows(prev => {
                // If this window is already open, move it to the top (end of the array)
                const isAlreadyOpen = prev.some(op => op.matricula === operator.matricula);
                if (isAlreadyOpen) {
                    const filtered = prev.filter(op => op.matricula !== operator.matricula);
                    return [...filtered, prev.find(op => op.matricula === operator.matricula)];
                }
                return [...prev, operator];
            });
        } else {
            console.warn("Attempted to open map for operator without coordinates");
        }
    };

    const handleCloseMap = (matricula) => {
        if (fullscreenMap === matricula && document.fullscreenElement) {
            document.exitFullscreen().catch(() => { });
        }
        delete mapWindowRefs.current[matricula];
        setOpenMapWindows(prev => prev.filter(op => op.matricula !== matricula));
    };

    const handleToggleMapFullscreen = async (matricula) => {
        const mapWindow = mapWindowRefs.current[matricula];
        if (!mapWindow) return;

        try {
            if (document.fullscreenElement === mapWindow) {
                await document.exitFullscreen();
            } else {
                await mapWindow.requestFullscreen();
            }
        } catch (error) {
            console.error("Error al cambiar la pantalla completa del mapa:", error);
        }
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            const fullscreenWindow = Object.entries(mapWindowRefs.current)
                .find(([, element]) => element === document.fullscreenElement);
            setFullscreenMap(fullscreenWindow ? fullscreenWindow[0] : null);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const updateOperatorLocations = async (matriculas, showLoading = false) => {
        if (!matriculas || matriculas.length === 0) return;

        if (showLoading) {
            setRefreshingLocations(prev => matriculas.reduce((next, matricula) => ({ ...next, [matricula]: true }), { ...prev }));
        }

        try {
            const response = await apiClient.post(`/api/tracking/status`, { matriculas });
            const data = response.data;

            if (data && data.length > 0) {
                setOpenMapWindows(prevWindows => prevWindows.map(prev => {
                    const item = data.find(d => d.matricula === prev.matricula);
                    if (!item) return prev;
                    return {
                        ...prev,
                        latitud: item.latitud ?? prev.latitud,
                        longitud: item.longitud ?? prev.longitud,
                        fecha_gps: item.fecha_gps || prev.fecha_gps,
                        municipio: item.ubicacion_detallada && item.ubicacion_detallada !== "Ubicación no disponible"
                            ? item.ubicacion_detallada
                            : prev.municipio
                    };
                }));

                setResults(prevResults => prevResults.map(result => {
                    const item = data.find(d => d.matricula === result.matricula);
                    return item ? {
                        ...result,
                        latitud: item.latitud ?? result.latitud,
                        longitud: item.longitud ?? result.longitud,
                        fecha_gps: item.fecha_gps || result.fecha_gps
                    } : result;
                }));
            }
        } catch (error) {
            console.error("Error al actualizar la ubicación de los operadores:", error);
        } finally {
            if (showLoading) {
                setRefreshingLocations(prev => matriculas.reduce((next, matricula) => {
                    const updated = { ...next };
                    delete updated[matricula];
                    return updated;
                }, { ...prev }));
            }
        }
    };

    // Using openMapWindowsRef to safely read current state inside the setInterval closure without passing full dependency array
    const openMapWindowsRef = useRef([]);
    useEffect(() => { openMapWindowsRef.current = openMapWindows; }, [openMapWindows]);

    // Auto-refresh coordinates for the selected operators in the Map Modals
    useEffect(() => {
        let intervalId;

        if (openMapWindows.length > 0) {
            // Fetch updated location
            const fetchLatestLocation = async () => {
                const matriculasToUpdate = openMapWindowsRef.current.map(op => op.matricula);
                await updateOperatorLocations(matriculasToUpdate);
            };

            // Run every 20 seconds while any modal is open
            intervalId = setInterval(fetchLatestLocation, 20000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [openMapWindows.length, API_URL]); // Solo dependemos de .length y API_URL para que no se reinicie el intervalo cada que recibimos actualizacion de coordenadas.

    // Calculate distance helper
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        // Haversine formula
        if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
        const R = 6371; // Radio de la tierra en km
        const dLat = deg2rad(lat2 - lat1);
        const dLon = deg2rad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c; // Distancia en km
        return d;
    }

    const deg2rad = (deg) => {
        return deg * (Math.PI / 180)
    }

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const sortedResults = React.useMemo(() => {
        let sortableItems = [...results];

        // Filtering by route
        if (selectedRouteFilter) {
            sortableItems = sortableItems.filter(item =>
                item.origen === selectedRouteFilter.origen &&
                item.destino === selectedRouteFilter.destino
            );
        }

        // Table filters
        if (tableFilters.matricula) {
            const val = tableFilters.matricula.toLowerCase();
            sortableItems = sortableItems.filter(i => (i.matricula?.toLowerCase().includes(val) || i.nombre?.toLowerCase().includes(val)));
        }
        if (tableFilters.ot) {
            const val = tableFilters.ot.toLowerCase();
            sortableItems = sortableItems.filter(i => (i.ot?.toLowerCase().includes(val) || i.origen?.toLowerCase().includes(val) || i.destino?.toLowerCase().includes(val)));
        }
        if (tableFilters.municipio) {
            const val = tableFilters.municipio.toLowerCase();
            sortableItems = sortableItems.filter(i => i.municipio?.toLowerCase().includes(val));
        }
        if (tableFilters.estadoPersonal) {
            const val = tableFilters.estadoPersonal.toLowerCase();
            sortableItems = sortableItems.filter(i => i.estadoPersonal?.toLowerCase().includes(val));
        }
        if (tableFilters.progreso) {
            if (tableFilters.progreso === 'sin_coordenadas') {
                sortableItems = sortableItems.filter(i => i.ot && i.ot !== "Sin Asignar" && (!i.latitud || !(i.distTotal > 0)));
            }
            else if (tableFilters.progreso === 'correctas') {
                sortableItems = sortableItems.filter(i => i.ot && i.ot !== "Sin Asignar" && i.latitud && i.longitud && i.distTotal > 0);
            }
            else {
                const val = parseFloat(tableFilters.progreso);
                if (!isNaN(val)) {
                    sortableItems = sortableItems.filter(i => i.progress !== undefined && i.progress !== null && i.progress >= val);
                }
            }
        }
        if (tableFilters.ot_prev) {
            const val = tableFilters.ot_prev.toLowerCase();
            sortableItems = sortableItems.filter(i => (i.ot_prev?.id?.toLowerCase().includes(val) || i.ot_prev?.origen?.toLowerCase().includes(val) || i.ot_prev?.destino?.toLowerCase().includes(val)));
        }
        if (tableFilters.ot_next) {
            const val = tableFilters.ot_next.toLowerCase();
            sortableItems = sortableItems.filter(i => (i.ot_next?.id?.toLowerCase().includes(val) || i.ot_next?.origen?.toLowerCase().includes(val) || i.ot_next?.destino?.toLowerCase().includes(val)));
        }

        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Manejo especial para objetos anidados (ot_prev/ot_next)
                if (sortConfig.key === 'ot_prev' || sortConfig.key === 'ot_next') {
                    aValue = aValue ? aValue.id : '';
                    bValue = bValue ? bValue.id : '';
                }

                // Manejo especial para progreso (numérico)
                if (sortConfig.key === 'progress') {
                    aValue = parseFloat(aValue) || 0;
                    bValue = parseFloat(bValue) || 0;
                }

                // Intentar ordenar numéricamente si es posible
                if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue)) && isFinite(aValue) && isFinite(bValue)) {
                    aValue = parseFloat(aValue);
                    bValue = parseFloat(bValue);
                } else {
                    // Si son strings, minúsculas para case-insensitive
                    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [results, sortConfig, selectedRouteFilter, tableFilters]);

    // Estadísticas
    const statistics = React.useMemo(() => {
        if (!results || results.length === 0) return null;

        let mtyCount = 0;
        let sahagunCount = 0;
        const routeCounts = {};

        results.forEach(item => {
            if (item.ot && item.ot !== "Sin Asignar") {
                // Lógica para bases según 4to dígito
                const otStr = item.ot.toString().trim();
                const numPart = otStr.replace(/^(OT-|OT)/i, '');

                // "4to dígito" -> índice 3 
                if (numPart.length >= 4) {
                    const digit = numPart.charAt(3);
                    if (digit === '0') mtyCount++;
                    else if (digit === '1') sahagunCount++;
                }

                // Stats de Rutas (Origen -> Destino)
                if (item.origen && item.destino) {
                    const key = `${item.origen}|${item.destino}`;
                    routeCounts[key] = (routeCounts[key] || 0) + 1;
                }
            }
        });

        // Rutas Agrupadas
        const rutasAgrupadas = Object.entries(routeCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([key, count]) => {
                const [origen, destino] = key.split('|');
                return { origen, destino, count };
            });

        return {
            total: results.length,
            withGPS: results.filter(r => r.latitud && r.longitud).length,
            active: results.filter(r => r.ot && r.ot !== "Sin Asignar").length,
            mty: mtyCount,
            sahagun: sahagunCount,
            rutasAgrupadas: rutasAgrupadas
        };
    }, [results]);

    const getSortIcon = (name) => {
        if (sortConfig.key !== name) {
            return <span key="sort-none"><i className="fas fa-sort text-muted ml-1" style={{ opacity: 0.5 }}></i></span>;
        }
        if (sortConfig.direction === 'ascending') {
            return <span key="sort-up"><i className="fas fa-sort-up text-primary ml-1"></i></span>;
        }
        return <span key="sort-down"><i className="fas fa-sort-down text-primary ml-1"></i></span>;
    };

    const handleTableFilterChange = (field, value) => {
        setTableFilters(prev => ({ ...prev, [field]: value }));
    };

    const clearTableFilters = () => {
        setTableFilters({ matricula: '', ot: '', municipio: '', progreso: '', estadoPersonal: '', ot_prev: '', ot_next: '' });
        setSelectedRouteFilter(null);
    };

    const handleExportToExcel = async () => {
        try {
            // Importar dinámicamente para optimizar bundle
            const XLSX = await import('xlsx');

            // 1. PREPARAR DATOS - Mapear a estructura atómica
            const exportData = sortedResults.map((item) => ({
                'Matrícula': item.matricula || '',
                'Nombre': item.nombre || '',
                'Estado Personal': item.estadoPersonal ? item.estadoPersonal.replace(/\|/g, ', ') : '',
                'OT Actual': item.ot || '—',
                'Origen': item.origen || '',
                'Destino': item.destino || '',
                'Municipio Actual': item.municipio || '',
                'Última Ubicación (Hora)': item.fecha_gps
                    ? new Date(item.fecha_gps).toLocaleString('es-MX', {
                        timeZone: 'America/Mexico_City',
                        dateStyle: 'short',
                        timeStyle: 'medium'
                    })
                    : '—',
                'Latitud': item.latitud ? parseFloat(item.latitud).toFixed(6) : '',
                'Longitud': item.longitud ? parseFloat(item.longitud).toFixed(6) : '',
                'Km Recorridos': item.distRecorrida ? parseFloat(item.distRecorrida).toFixed(1) : '',
                'Km Totales': item.distTotal ? parseFloat(item.distTotal).toFixed(1) : '',
                'Avance (%)': item.progress ? parseFloat(item.progress).toFixed(1) : '',
                'Último Traslado (OT)': item.ot_prev?.id || '—',
                'Último Traslado (Origen)': item.ot_prev?.origen || '',
                'Último Traslado (Destino)': item.ot_prev?.destino || '',
                'Próximo Traslado (OT)': item.ot_next?.id || '—',
                'Próximo Traslado (Origen)': item.ot_next?.origen || '',
                'Próximo Traslado (Destino)': item.ot_next?.destino || '',
            }));

            // 2. CREAR WORKBOOK
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Rastreo Operadores");

            // 3. APLICAR ESTILOS Y FORMATO
            // Ancho de columnas
            ws['!cols'] = [
                { wch: 12 },  // Matrícula
                { wch: 18 },  // Nombre
                { wch: 20 },  // Estado Personal
                { wch: 15 },  // OT Actual
                { wch: 18 },  // Origen
                { wch: 18 },  // Destino
                { wch: 25 },  // Municipio
                { wch: 22 },  // Última Ubicación
                { wch: 12 },  // Latitud
                { wch: 12 },  // Longitud
                { wch: 12 },  // Km Recorridos
                { wch: 12 },  // Km Totales
                { wch: 10 },  // Avance
                { wch: 15 },  // Último Traslado OT
                { wch: 18 },  // Último Origen
                { wch: 18 },  // Último Destino
                { wch: 15 },  // Próximo OT
                { wch: 18 },  // Próximo Origen
                { wch: 18 }   // Próximo Destino
            ];

            // 4. AGREGAR METADATA - Hoja de resumen
            const metadataSheet = XLSX.utils.aoa_to_sheet([
                ['EXPORTACIÓN DE RESULTADOS - RASTREO DE OPERADORES'],
                [],
                ['Fecha de exportación:', new Date().toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })],
                ['Total de registros:', sortedResults.length],
                ['Operadores con GPS activo:', results.filter(r => r.latitud && r.longitud).length],
                ['Operadores en ruta:', results.filter(r => r.ot && r.ot !== "Sin Asignar").length],
                [],
                ['FILTROS APLICADOS:'],
                ['Matrícula:', tableFilters.matricula || '(ninguno)'],
                ['OT/Ruta:', tableFilters.ot || '(ninguno)'],
                ['Municipio:', tableFilters.municipio || '(ninguno)'],
                ['Estado Personal:', tableFilters.estadoPersonal || '(ninguno)'],
                ['Progreso:', tableFilters.progreso || '(ninguno)'],
                ['Ruta Origen → Destino:', selectedRouteFilter ? `${selectedRouteFilter.origen} → ${selectedRouteFilter.destino}` : '(ninguno)'],
                [],
                ['ORDEN ACTUAL:', sortConfig.key ? `${sortConfig.key} (${sortConfig.direction})` : 'Sin ordenamiento']
            ]);

            XLSX.utils.book_append_sheet(wb, metadataSheet, "Resumen");

            // 5. GENERAR NOMBRE DE ARCHIVO
            const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const filtroTag = Object.values(tableFilters).some(v => v !== '')
                ? '_filtrado'
                : '';
            const filename = `Rastreo_Operadores_${timestamp}${filtroTag}.xlsx`;

            // 6. DESCARGAR
            XLSX.writeFile(wb, filename);

        } catch (error) {
            console.error('Error al exportar:', error);
            alert('Error al exportar los datos. Verifica la consola.');
        }
    };

    const handleTrackWithMatriculas = async (matriculas) => {
        if (!matriculas || matriculas.length === 0) {
            setError("Por favor, ingresa al menos una matrícula.");
            return;
        }
        setLoading(true);
        setError(null);
        setResults([]);
        setSelectedRouteFilter(null);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('matriculas', matriculas.join(','));
            return next;
        });
        // Continuar con la lógica de rastreo usando las matrículas recibidas
        await handleTrackCore(matriculas);
    };

    const handleTrack = async () => {
        setLoading(true);
        setError(null);
        setResults([]);
        setSelectedRouteFilter(null);

        // Limpiar entrada: dividir por saltos de línea y eliminar vacíos
        const matriculas = matriculasText.split(/\r?\n/).map(m => m.trim()).filter(m => m);

        if (matriculas.length === 0) {
            setError("Por favor, ingresa al menos una matrícula.");
            setLoading(false);
            return;
        }

        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('matriculas', matriculas.join(','));
            return next;
        });

        await handleTrackCore(matriculas);
    };

    const handleTrackCore = async (matriculas) => {
        try {
            // 1. Obtener estatus inicial desde API propia
            const response = await apiClient.post(`/api/tracking/status`, { matriculas });
            let trackingData = response.data;

            // 2. Enriquecer con municipio y cálculo de progreso
            // Se hace en paralelo con Promise.all() para mejorar rendimiento
            const enrichedData = await Promise.all(trackingData.map(async (item) => {
                // Priorizar la ubicación detallada que viene del backend (OpenCage)
                let municipio = item.ubicacion_detallada && item.ubicacion_detallada !== "Ubicación no disponible"
                    ? item.ubicacion_detallada
                    : "Buscando ubicación...";

                let progress = 0;
                let distTotal = 0;
                let distRecorrida = 0;
                let distanceCalculated = false;

                // Si hay coordenadas GPS
                if (item.latitud && item.longitud) {

                    // Solo consultar INEGI si NO tenemos ubicación detallada de OpenCage
                    if (municipio === "Buscando ubicación..." || municipio === "Ubicación no disponible") {
                        try {
                            const muniRes = await apiClient.get(`/api/tracking/municipality`, {
                                params: {
                                    lat: item.latitud,
                                    lon: item.longitud,
                                    escala: escala // Sending current scale from slider
                                }
                            });

                            const inegiData = muniRes.data;
                            if (inegiData && inegiData.data && inegiData.data.length > 0) {
                                const lugar = inegiData.data[0];
                                municipio = `${lugar.nombre_geografico}, ${lugar.entidad_federativa}`;
                            } else if (inegiData.data && inegiData.data.nombre) {
                                municipio = inegiData.data.nombre;
                            } else {
                                // Fallback final
                                municipio = "Ubicación desconocida";
                            }
                        } catch (e) {
                            console.error(`Error obteniendo municipio para ${item.matricula}`, e);
                            municipio = "Error de Red (INEGI)";
                        }
                    }

                    // Calcular Progreso si hay ruta definida (coords origen/destino)
                    if (item.lat_origen && item.lon_origen && item.lat_destino && item.lon_destino) {
                        distTotal = calculateDistance(item.lat_origen, item.lon_origen, item.lat_destino, item.lon_destino);

                        // Distancia restante al destino (lineal)
                        const distRestante = calculateDistance(item.latitud, item.longitud, item.lat_destino, item.lon_destino);

                        // Progreso lineal: (Total - Restante) / Total
                        if (distTotal > 0) {
                            progress = ((distTotal - distRestante) / distTotal) * 100;
                        }

                        // Ajustes visuales (clip)
                        if (progress < 0) progress = 0;
                        if (progress > 100) progress = 100;

                        // Calculamos distancia recorrida "estimada"
                        distRecorrida = distTotal - distRestante;
                        if (distRecorrida < 0) distRecorrida = 0;
                    }
                } else {
                    municipio = "Sin señal GPS";
                }

                return {
                    ...item,
                    municipio,
                    progress: progress.toFixed(1),
                    distTotal: distTotal ? distTotal.toFixed(1) : 0,
                    distRecorrida: distRecorrida ? distRecorrida.toFixed(1) : 0
                };
            }));

            // Actualizamos el estado con los datos enriquecidos
            setResults(enrichedData);

        } catch (err) {
            console.error(err);
            setError("Ocurrió un error al consultar el servicio de rastreo. Verifica tu conexión.");
        } finally {
            setLoading(false);
        }
    };

    const handleLinkCruce = (cruceId, casetaEsperadaId) => {
        setManualLinks(prev => {
            const newLinks = { ...prev };
            if (casetaEsperadaId) {
                newLinks[cruceId] = casetaEsperadaId;
            } else {
                delete newLinks[cruceId];
            }
            return newLinks;
        });
    };

    const handleSaveLinks = async () => {
        if (Object.keys(manualLinks).length === 0) return;
        setSavingLinks(true);

        try {
            const payload = {
                ot: casetasData.ot,
                id_clave: casetasData.id_clave, // Ocupamos la clave para sacar el importe
                vinculos: Object.entries(manualLinks).map(([cruceId, idCasetaNueva]) => {
                    return {
                        cruceId: cruceId, // Ya es un varChar en DB
                        nuevoIdCaseta: idCasetaNueva // numeric de la caseta oficial
                    };
                })
            };


            await apiClient.put(`/api/cruces/vincularManual`, payload);

            // Notificamos éxito y reseteamos el estado
            setManualLinks({});

            // Recargar la info para ver actualizados los estatus del backend:
            handleOpenCasetas(casetasData.ot, casetasData.id_tipo_ruta, null, casetasData.id_clave, casetasData.id_usuario);

        } catch (error) {
            console.error("Error guardando vinculaciones:", error);
            alert("Hubo un error al guardar los vínculos. Asegúrate de tener red.");
        } finally {
            setSavingLinks(false);
        }
    };

    // Opción 1: Asignar OT manualmente a un cruce desde el modal del TAG
    const handleAssignOTtoTagCruce = async (cruceId, otValue) => {
        const ot = otValue?.trim();
        if (!ot) return;
        const otRegex = /^OT-\d+$/;
        if (!otRegex.test(ot)) {
            alert('Formato inválido. Usa el formato: OT-XXXXX (ejemplo: OT-605092)');
            return;
        }
        setTagSavingOT(prev => new Set([...prev, cruceId]));
        try {
            await apiClient.patch(`/api/cruces/${cruceId}/setOT`, { OT: ot });
            setTagOTInputs(prev => { const n = { ...prev }; delete n[cruceId]; return n; });
            // Recargar el modal del TAG para reflejar el cambio
            handleOpenTagModal(tagModalData.tag);
        } catch (err) {
            alert('Error al asignar la OT. Intenta de nuevo.');
        } finally {
            setTagSavingOT(prev => { const s = new Set(prev); s.delete(cruceId); return s; });
        }
    };

    return (
        <div className="container-fluid">
            <h1 className="h3 mb-4 text-gray-800">Función para rastreo de Operadores <b>(Auxiliar)</b></h1>

            <div className="row" style={{ marginBottom: '-2rem', }}>
                {/* Panel de Entrada */}
                <div className="col-xl-3 col-lg-4">
                    <div className="card shadow mb-4">
                        <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
                            <h6 className="m-0 font-weight-bold text-primary">Búsqueda de Unidades</h6>
                        </div>
                        <div className="card-body">
                            <form onSubmit={(e) => { e.preventDefault(); handleTrack(); }}>
                                <div className="form-group">
                                    <label style={{ 'fontSize': '.875rem', }}>Selecciona el operador de la siguiente lista <br />(para agregarlo en la consulta):</label>
                                    <select
                                        className="form-control mb-3"
                                        value={selectedOpDropdown}
                                        onChange={handleSelectOperatorChange}
                                    >
                                        <option value="">-- Selecciona un operador --</option>
                                        {operadores.map(op => (
                                            <option key={op.id} value={op.id}>
                                                {op.nombre} ({op.id})
                                            </option>
                                        ))}
                                    </select>
                                    <div className="d-flex justify-content-end mb-2">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm mb-3"
                                            onClick={handleAddAllOperators}
                                        >
                                            Agregar todas las matrículas ({operadores.length})
                                        </button>


                                    </div>
                                    <label htmlFor="matriculasInput">Ingresa las matrículas que deseas rastrear. <br /><i className='text-muted'>(una por línea):</i></label>
                                    <textarea
                                        className="form-control"
                                        id="matriculasInput"
                                        rows="10"
                                        placeholder="Ejemplo:&#10;2333&#10;1818&#10;2358"
                                        value={matriculasText}
                                        onChange={(e) => setMatriculasText(e.target.value)}
                                        style={{ fontFamily: 'monospace' }}
                                    ></textarea>
                                    <small className="form-text text-muted">Coloca las matrículas de los operadores.</small>
                                </div>
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-block"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <span key="loading"><i className="fas fa-spinner fa-spin"></i> Consultando...</span>
                                    ) : (
                                        <span key="search"><i className="fas fa-search-location"></i> Rastrear Unidades</span>
                                    )}
                                </button>
                            </form>

                            {error && (
                                <div className="alert alert-danger mt-3" role="alert">
                                    <i className="fas fa-exclamation-circle"></i> {error}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Panel de Resultados */}
                <div className="col-xl-9 col-lg-7">
                    <div className="row">
                        {/* Sección de Estadísticas */}
                        {statistics && results.length > 0 && (
                            <>
                                <div className="col-6 mb-2">
                                    {/* Card MTY */}
                                    <div className="col-xl-8 col-md-12 mb-2">
                                        <div className="card border-left-primary shadow py-0" style={{ minHeight: 'auto' }}>
                                            <div className="card-body p-2">
                                                <div className="row no-gutters align-items-center">
                                                    <div className="col mr-2">
                                                        <div className="text-xs font-weight-bold text-primary text-uppercase mb-0">
                                                            Base Monterrey</div>
                                                        <div className="h6 mb-0 font-weight-bold text-gray-800">{statistics.mty}</div>
                                                    </div>
                                                    <div className="col-auto">
                                                        <i className="fas fa-industry fa-lg text-gray-300"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Card Sahagún */}
                                    <div className="col-xl-8 col-md-12 mb-2">
                                        <div className="card border-left-success shadow py-0" style={{ minHeight: 'auto' }}>
                                            <div className="card-body p-2">
                                                <div className="row no-gutters align-items-center">
                                                    <div className="col mr-2">
                                                        <div className="text-xs font-weight-bold text-success text-uppercase mb-0">
                                                            Base Sahagún</div>
                                                        <div className="h6 mb-0 font-weight-bold text-gray-800">{statistics.sahagun}</div>
                                                    </div>
                                                    <div className="col-auto">
                                                        <i className="fas fa-warehouse fa-lg text-gray-300"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Card Resumen */}
                                    <div className="col-xl-8 col-md-12 mb-2">
                                        <div className="card border-left-info shadow py-0" style={{ minHeight: 'auto' }}>
                                            <div className="card-body p-2">
                                                <div className="row no-gutters align-items-center">
                                                    <div className="col mr-2">
                                                        <div className="text-xs font-weight-bold text-info text-uppercase mb-0">Resumen</div>
                                                        <div className="h6 mb-0 font-weight-bold text-gray-800">
                                                            {statistics.active} <span className="text-xs text-gray-500 font-weight-light">En ruta</span>
                                                            <span className="mx-1 text-gray-300">|</span>
                                                            <span className="text-xs text-gray-500 font-weight-light">{statistics.withGPS}/{statistics.total} GPS</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-auto">
                                                        {/* Al darle clic al icono de resumen de GPSs vamos a filtrar las matriculas que tienen GPS inactivo */}

                                                        <i onClick={handleFilterGPSInactive} className="fas fa-chart-pie fa-lg text-gray-300"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Rutas Agrupadas */}
                                {statistics.rutasAgrupadas.length > 0 && (
                                    <div className="col-5 mb-2">
                                        <div className="wrapper" style={{ maxHeight: '11.5rem', overflow: 'auto', paddingBottom: '1rem' }}>


                                            <div className="card shadow" style={{ minHeight: '5rem' }}>
                                                <div className="card-header py-2 d-flex flex-row align-items-center justify-content-between" style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10 }}>
                                                    <h6 className="m-0 font-weight-bold text-secondary text-xs">
                                                        <i className="fas fa-route mr-2"></i> Rutas agrupadas
                                                    </h6>
                                                </div>
                                                <ul className="list-group list-group-flush small">
                                                    {statistics.rutasAgrupadas.map((route, idx) => {
                                                        const isSelected = selectedRouteFilter && selectedRouteFilter.origen === route.origen && selectedRouteFilter.destino === route.destino;
                                                        return (
                                                            <li
                                                                key={idx}
                                                                className={`list-group-item d-flex justify-content-between align-items-center py-1 ${isSelected ? 'bg-gray-200' : ''}`}
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => setSelectedRouteFilter(isSelected ? null : { origen: route.origen, destino: route.destino })}
                                                                title="Filtrar por esta ruta"
                                                            >
                                                                <div>
                                                                    <strong>{route.origen}</strong>
                                                                    <i className="fas fa-arrow-right mx-2 text-gray-400"></i>
                                                                    <strong>{route.destino}</strong>
                                                                </div>
                                                                <span className={`badge badge-pill ${isSelected ? 'badge-primary' : 'badge-secondary'}`} style={{ fontSize: '0.8rem' }}>{route.count}</span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                )}
                            </>
                        )}
                    </div>
                    <div className="card shadow mb-4">
                        <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
                            <h6 className="m-0 font-weight-bold text-primary">Resultadoss del Rastreo - <span className='text-muted font-weight-bolder'>({sortedResults.length})</span></h6>
                            <div className="d-flex gap-2">
                                {(Object.values(tableFilters).some(v => v !== '') || selectedRouteFilter) && (
                                    <button type="button" className="btn btn-sm btn-outline-danger py-0 px-2 font-weight-bold" title="Limpiar filtros" onClick={clearTableFilters}>
                                        <i className="fas fa-times"></i> Limpiar Filtros
                                    </button>
                                )}
                                {sortedResults.length > 0 && (
                                    <button type="button" className="btn btn-sm btn-outline-success py-0 px-2 font-weight-bold" title="Exportar resultados a Excel" onClick={handleExportToExcel}>
                                        <i className="fas fa-download"></i> Exportar
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="card-body">
                            {results.length > 0 ? (
                                <div key="results" className="table-responsive table-container table-scroll">
                                    <table className="table table-bordered table-striped" width="100%" cellSpacing="0">
                                        <thead className="thead-light">
                                            <tr>
                                                <th onClick={() => requestSort('matricula')} style={{ cursor: 'pointer' }}>
                                                    Operador {getSortIcon('matricula')}
                                                </th>
                                                <th onClick={() => requestSort('estadoPersonal')} style={{ cursor: 'pointer' }}>
                                                    Estado del personal {getSortIcon('estadoPersonal')}
                                                </th>
                                                <th onClick={() => requestSort('ot')} style={{ cursor: 'pointer' }}>
                                                    Ruta / OT {getSortIcon('ot')}
                                                </th>
                                                <th onClick={() => requestSort('municipio')} style={{ cursor: 'pointer' }}>
                                                    Ubicación Actual {getSortIcon('municipio')}
                                                </th>
                                                <th onClick={() => requestSort('progress')} style={{ cursor: 'pointer' }}>
                                                    Progreso Estimado {getSortIcon('progress')}
                                                </th>
                                                <th onClick={() => requestSort('ot_prev')} style={{ cursor: 'pointer' }}>
                                                    Último traslado realizado {getSortIcon('ot_prev')}
                                                </th>
                                                <th onClick={() => requestSort('ot_next')} style={{ cursor: 'pointer' }}>
                                                    Traslado programado {getSortIcon('ot_next')}
                                                </th>
                                                <th>
                                                    OTs
                                                </th>
                                            </tr>
                                            <tr className="bg-light shadow-sm">
                                                <th className="p-1">
                                                    <input name='txtMatricula' type="text" className="form-control form-control-sm" placeholder=" Filtrar..." value={tableFilters.matricula} onChange={(e) => handleTableFilterChange('matricula', e.target.value)} />
                                                </th>
                                                <th className="p-1">
                                                    <input name='EstadoPersonal' type="text" className="form-control form-control-sm" placeholder=" Filtrar..." value={tableFilters.estadoPersonal} onChange={(e) => handleTableFilterChange('estadoPersonal', e.target.value)} />
                                                </th>
                                                <th className="p-1">
                                                    <input name='OT' type="text" className="form-control form-control-sm" placeholder=" Filtrar..." value={tableFilters.ot} onChange={(e) => handleTableFilterChange('ot', e.target.value)} />
                                                </th>
                                                <th className="p-1">
                                                    <input name='Municipio' type="text" className="form-control form-control-sm" placeholder=" Filtrar..." value={tableFilters.municipio} onChange={(e) => handleTableFilterChange('municipio', e.target.value)} />
                                                </th>
                                                <th className="p-1 text-center text-muted small align-middle">
                                                    <select
                                                        className="form-control form-control-sm"
                                                        value={tableFilters.progreso}
                                                        onChange={(e) => handleTableFilterChange('progreso', e.target.value)}
                                                    >
                                                        <option value="">Todos</option>
                                                        <option value="sin_coordenadas">Por corregir</option>
                                                        <option value="correctas">Correctas</option>
                                                    </select>
                                                </th>
                                                <th className="p-1">
                                                    <input name='OTPrev' type="text" className="form-control form-control-sm" placeholder=" Filtrar..." value={tableFilters.ot_prev} onChange={(e) => handleTableFilterChange('ot_prev', e.target.value)} />
                                                </th>
                                                <th className="p-1">
                                                    <input name='OTNext' type="text" className="form-control form-control-sm" placeholder=" Filtrar..." value={tableFilters.ot_next} onChange={(e) => handleTableFilterChange('ot_next', e.target.value)} />
                                                </th>
                                                <th className="p-1">
                                                    
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedResults.map((item, index) => (
                                                <tr key={item.matricula || index}>
                                                    <td className="align-middle">
                                                        {item.latitud && item.longitud ? (
                                                            <>
                                                                <div className="font-weight-bold text-primary">
                                                                    <i className="fas fa-map-marker-alt mr-1"></i>
                                                                    {item.matricula}
                                                                </div>
                                                                <div className="small text-gray-600" style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                    title="Clic para ver datos del operador"
                                                                    onClick={() => handleOpenOperadorModal(item.matricula, item.nombre)}>{item.nombre}</div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="font-weight-bold text-secondary">{item.matricula}</div>
                                                                <div className="small text-gray-600" style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                    title="Clic para ver datos del operador"
                                                                    onClick={() => handleOpenOperadorModal(item.matricula, item.nombre)}>{item.nombre}</div>
                                                            </>
                                                        )}
                                                    </td>
                                                    <td className="align-middle text-center">
                                                        {item.estadoPersonal ? (
                                                            item.estadoPersonal.split('|').map((estado, idx) => (
                                                                <span key={idx} className="badge badge-info d-block mb-1 text-wrap">
                                                                    {estado.trim()}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span className="small text-muted font-italic">-</span>
                                                        )}
                                                    </td>
                                                    <td className="align-middle">
                                                        {item.ot && item.ot !== "Sin Asignar" ? (
                                                            <>
                                                                <div className="d-flex align-items-center mb-1">
                                                                    <span
                                                                        className="badge badge-primary mr-2"
                                                                        style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                                                                        onClick={() => handleCopyOT(item.ot)}
                                                                        title="Clic para copiar la OT"
                                                                    >OT: {item.ot}</span>
                                                                    {item.id_tipo_ruta && (
                                                                        <button
                                                                            onClick={() => handleOpenCasetas(item.ot, item.id_tipo_ruta, item.tag, item.id_clave, item.id_usuario)}
                                                                            className="btn btn-sm btn-outline-info py-0 px-1 ml-1"
                                                                            title="Ver Casetas"
                                                                        >
                                                                            <i className="fas fa-road"></i>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                <div className="small">
                                                                    <strong
                                                                        className={item.origen_detalle ? "text-primary" : ""}
                                                                        style={item.origen_detalle ? { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '2px' } : {}}
                                                                        onClick={item.origen_detalle ? () => handleOpenDirModal(item.origen_detalle, 'Origen') : undefined}
                                                                        title={item.origen_detalle ? `Detalle: ${item.origen_detalle.razon_social || item.origen_detalle.nombre}` : ""}
                                                                    >{item.origen}</strong>
                                                                    <i className="fas fa-arrow-right text-gray-400 mx-1"></i>
                                                                    <strong
                                                                        className={item.destino_detalle ? "text-primary" : ""}
                                                                        style={item.destino_detalle ? { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '2px' } : {}}
                                                                        onClick={item.destino_detalle ? () => handleOpenDirModal(item.destino_detalle, 'Destino') : undefined}
                                                                        title={item.destino_detalle ? `Detalle: ${item.destino_detalle.razon_social || item.destino_detalle.nombre}` : ""}
                                                                    >{item.destino}</strong>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <span className="badge badge-secondary">Sin Asignación</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {item.latitud ? (
                                                            <button
                                                                className="btn btn-link p-0 text-left text-decoration-none"
                                                                onClick={() => handleOpenMap(item)}
                                                                title="Ver en mapa"
                                                            >
                                                                <div className="text-dark font-weight-bold">
                                                                    <i className="fas fa-map-marker-alt text-danger mr-1"></i>
                                                                    {item.municipio}
                                                                </div>
                                                                <div className="small text-muted" title={item.fecha_gps}>
                                                                    {item.fecha_gps ? new Date(item.fecha_gps).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }) : ''}
                                                                </div>
                                                            </button>
                                                        ) : (
                                                            <div className="text-warning small">
                                                                <i className="fas fa-exclamation-triangle"></i> Sin señal GPS
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ minWidth: '150px' }}>
                                                        {item.latitud && item.distTotal > 0 ? (
                                                            <div>
                                                                <div className="progress mb-1" style={{ height: '20px' }}>
                                                                    <div
                                                                        className={`progress-bar ${item.progress >= 95 ? 'bg-success' : 'bg-info'} progress-bar-striped progress-bar-animated`}
                                                                        role="progressbar"
                                                                        style={{ width: `${item.progress}%` }}
                                                                        aria-valuenow={item.progress}
                                                                        aria-valuemin="0"
                                                                        aria-valuemax="100"
                                                                    >
                                                                        {item.progress}%
                                                                    </div>
                                                                </div>
                                                                <div className="small text-muted d-flex justify-content-between">
                                                                    <span>{item.distRecorrida} km</span>
                                                                    <span>{item.distTotal} km</span>
                                                                </div>
                                                            </div>
                                                        ) : item.ot && item.ot !== "Sin Asignar" ? (
                                                            <div className="text-center">
                                                                <div className="small text-warning font-italic mb-1">
                                                                    <i className="fas fa-exclamation-circle text-warning mr-1"></i> Faltan geocoordenadas
                                                                </div>
                                                                <div className="small text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                                                    <strong>IDs Directorio:</strong> {item.id_origen || '?'} <i className="fas fa-arrow-right mx-1"></i> {item.id_destino || '?'}
                                                                </div>
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary py-0"
                                                                    style={{ fontSize: '0.75rem' }}
                                                                    onClick={() => {
                                                                        if (item.origen_detalle) handleOpenDirModal(item.origen_detalle, 'Origen');
                                                                        else if (item.destino_detalle) handleOpenDirModal(item.destino_detalle, 'Destino');
                                                                    }}
                                                                    title="Verificar direcciones"
                                                                >
                                                                    Ajustar ruta
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="small text-muted font-italic text-center">
                                                                N/A
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="align-middle border-left">
                                                        {item.ot_prev ? (
                                                            <>
                                                                <div className="d-flex align-items-center mb-1">
                                                                    <span
                                                                        className="badge badge-secondary mr-2"
                                                                        style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                                                                        onClick={() => handleCopyOT(item.ot_prev.id)}
                                                                        title="Clic para copiar la OT"
                                                                    >OT: {item.ot_prev.id}</span>
                                                                    {item.ot_prev.id_tipo_ruta && (
                                                                        <button
                                                                            onClick={() => handleOpenCasetas(item.ot_prev.id, item.ot_prev.id_tipo_ruta, item.tag, item.ot_prev.id_clave, item.ot_prev.id_usuario)}
                                                                            className="btn btn-sm btn-outline-secondary py-0 px-1 ml-1"
                                                                            title="Ver Casetas"
                                                                        >
                                                                            <i className="fas fa-road"></i>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                <div className="small text-muted">
                                                                    <strong
                                                                        className={item.ot_prev.origen_detalle ? "text-primary" : ""}
                                                                        style={item.ot_prev.origen_detalle ? { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '2px' } : {}}
                                                                        onClick={item.ot_prev.origen_detalle ? () => handleOpenDirModal(item.ot_prev.origen_detalle, 'Origen') : undefined}
                                                                        title={item.ot_prev.origen_detalle ? `Detalle: ${item.ot_prev.origen_detalle.razon_social || item.ot_prev.origen_detalle.nombre}` : ""}
                                                                    >{item.ot_prev.origen}</strong>
                                                                    <i className="fas fa-arrow-right mx-1"></i>
                                                                    <strong
                                                                        className={item.ot_prev.destino_detalle ? "text-primary" : ""}
                                                                        style={item.ot_prev.destino_detalle ? { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '2px' } : {}}
                                                                        onClick={item.ot_prev.destino_detalle ? () => handleOpenDirModal(item.ot_prev.destino_detalle, 'Destino') : undefined}
                                                                        title={item.ot_prev.destino_detalle ? `Detalle: ${item.ot_prev.destino_detalle.razon_social || item.ot_prev.destino_detalle.nombre}` : ""}
                                                                    >{item.ot_prev.destino}</strong>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <span className="small text-muted font-italic">-</span>
                                                        )}
                                                    </td>
                                                    <td className="align-middle">
                                                        {item.ot_next ? (
                                                            <>
                                                                <div className="d-flex align-items-center mb-1">
                                                                    <span
                                                                        className="badge badge-info mr-2"
                                                                        style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                                                                        onClick={() => handleCopyOT(item.ot_next.id)}
                                                                        title="Clic para copiar la OT"
                                                                    >OT prog: {item.ot_next.id}</span>
                                                                    {item.ot_next.id_tipo_ruta && (
                                                                        <button
                                                                            onClick={() => handleOpenCasetas(item.ot_next.id, item.ot_next.id_tipo_ruta, item.tag, item.ot_next.id_clave, item.ot_next.id_usuario)}
                                                                            className="btn btn-sm btn-outline-info py-0 px-1 ml-1"
                                                                            title="Ver Casetas"
                                                                        >
                                                                            <i className="fas fa-road"></i>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                <div className="small text-primary">
                                                                    <strong
                                                                        className={item.ot_next.origen_detalle ? "text-primary" : ""}
                                                                        style={item.ot_next.origen_detalle ? { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '2px' } : {}}
                                                                        onClick={item.ot_next.origen_detalle ? () => handleOpenDirModal(item.ot_next.origen_detalle, 'Origen') : undefined}
                                                                        title={item.ot_next.origen_detalle ? `Detalle: ${item.ot_next.origen_detalle.razon_social || item.ot_next.origen_detalle.nombre}` : ""}
                                                                    >{item.ot_next.origen}</strong>
                                                                    <i className="fas fa-arrow-right mx-1"></i>
                                                                    <strong
                                                                        className={item.ot_next.destino_detalle ? "text-primary" : ""}
                                                                        style={item.ot_next.destino_detalle ? { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: '2px' } : {}}
                                                                        onClick={item.ot_next.destino_detalle ? () => handleOpenDirModal(item.ot_next.destino_detalle, 'Destino') : undefined}
                                                                        title={item.ot_next.destino_detalle ? `Detalle: ${item.ot_next.destino_detalle.razon_social || item.ot_next.destino_detalle.nombre}` : ""}
                                                                    >{item.ot_next.destino}</strong>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <span className="small text-muted font-italic">-</span>
                                                        )}
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                                        <button className="btn btn-primary btn-sm" onClick={() => setOtsModal({ show: true, matricula: item.matricula, nombre: item.nombre })}>
                                                            Ver listado de OTs
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (loading) ? (
                                <div key="loading" className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Cargando...</span>
                                    </div>
                                    <p className="mt-2 text-muted">Rastreando unidades, por favor espere... </p>
                                </div>
                            ) :
                                (
                                    <div key="empty" className="text-center py-5">
                                        <div className="mb-3">
                                            <span className="fa-stack fa-2x">
                                                <i className="fas fa-circle fa-stack-2x text-gray-200"></i>
                                                <i className="fas fa-truck fa-stack-1x text-gray-500"></i>
                                            </span>
                                        </div>
                                        <p className="text-gray-500 mb-0">Ingrese matrículas para ver su ubicación y estatus.</p>
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Ventanas Móviles de Mapa */}
            {openMapWindows.map((selectedOperator, index) => (
                <Rnd
                    key={`map-window-${selectedOperator.matricula}`}
                    default={{
                        x: window.innerWidth > 600 ? ((window.innerWidth - 600) / 2) + (index * 30) : 10,
                        y: window.innerHeight > 550 ? ((window.innerHeight - 550) / 2) + (index * 30) : 10,
                        width: window.innerWidth > 600 ? 600 : window.innerWidth - 20,
                        height: window.innerHeight > 550 ? 550 : window.innerHeight - 20,
                    }}
                    minWidth={300}
                    minHeight={300}
                    bounds="window"
                    dragHandleClassName="handle-drag"
                    cancel=".close"
                    style={{ zIndex: 1060 + index, position: 'fixed' }}
                    enableResizing={{ top: true, right: true, bottom: true, left: true, topRight: true, bottomRight: true, bottomLeft: true, topLeft: true }}
                >
                    <div
                        ref={element => { mapWindowRefs.current[selectedOperator.matricula] = element; }}
                        className="card shadow-lg h-100"
                        style={{ border: '1px solid rgba(0,0,0,.2)', borderRadius: '0.3rem', overflow: 'hidden', backgroundColor: '#fff' }}
                    >
                        <div
                            className="card-header bg-white d-flex align-items-center justify-content-between handle-drag"
                            style={{ cursor: 'move', userSelect: 'none', padding: '0.75rem 1rem', borderBottom: '1px solid #e3e6f0' }}
                            onMouseDown={() => {
                                // Traer al frente al hacer clic en el header
                                setOpenMapWindows(prev => {
                                    const filtered = prev.filter(op => op.matricula !== selectedOperator.matricula);
                                    return [...filtered, prev.find(op => op.matricula === selectedOperator.matricula)];
                                });
                            }}
                        >
                            <h6 className="m-0 font-weight-bold text-primary">
                                <i className="fas fa-satellite-dish mr-2"></i>
                                Ubicación: {selectedOperator?.matricula} - {selectedOperator?.nombre}
                            </h6>
                            <div className="d-flex align-items-center">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-info mr-2"
                                    onClick={() => updateOperatorLocations([selectedOperator.matricula], true)}
                                    disabled={!!refreshingLocations[selectedOperator.matricula]}
                                    title="Actualizar ubicación"
                                    aria-label="Actualizar ubicación"
                                >
                                    <i className={`fas ${refreshingLocations[selectedOperator.matricula] ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary mr-2"
                                    onClick={() => handleToggleMapFullscreen(selectedOperator.matricula)}
                                    title={fullscreenMap === selectedOperator.matricula ? "Salir de pantalla completa" : "Ver en pantalla completa"}
                                    aria-label={fullscreenMap === selectedOperator.matricula ? "Salir de pantalla completa" : "Ver en pantalla completa"}
                                >
                                    <i className={`fas ${fullscreenMap === selectedOperator.matricula ? 'fa-compress' : 'fa-expand'}`}></i>
                                </button>
                                <button type="button" className="close" aria-label="Close" onClick={() => handleCloseMap(selectedOperator.matricula)}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                        </div>
                        <div className="card-body p-0 d-flex flex-column h-100" style={{ position: 'relative' }}>
                            {selectedOperator && selectedOperator.latitud && selectedOperator.longitud ? (
                                <MapContainer
                                    key={selectedOperator.matricula}
                                    center={[parseFloat(selectedOperator.latitud), parseFloat(selectedOperator.longitud)]}
                                    zoom={9}
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <ResizeAwareMap />
                                    <MapViewport latitude={selectedOperator.latitud} longitude={selectedOperator.longitud} />
                                    <TileLayer
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    />
                                    {selectedOperator.rutas && selectedOperator.rutas.length > 0 && (
                                        <Polyline positions={selectedOperator.rutas.map(p => [parseFloat(p.latitud), parseFloat(p.longitud)])} color="blue" />
                                    )}
                                    <Marker position={[parseFloat(selectedOperator.latitud), parseFloat(selectedOperator.longitud)]}>
                                        <Popup>
                                            <div>
                                                <strong>{selectedOperator.matricula}</strong> <br />
                                                {selectedOperator.nombre} <br />
                                                <hr className="my-1" />
                                                {selectedOperator.municipio} <br />
                                                <small className="text-muted">{selectedOperator.fecha_gps ? new Date(selectedOperator.fecha_gps).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }) : ''}</small>
                                            </div>
                                        </Popup>
                                    </Marker>
                                </MapContainer>
                            ) : (
                                <div className="text-center py-5">
                                    <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                                    <p>No hay coordenadas disponibles para este operador.</p>
                                </div>
                            )}
                        </div>
                        <div className="card-footer py-2 d-flex justify-content-between align-items-center bg-light">
                            <span className="text-muted small text-truncate mr-2" title={selectedOperator?.municipio}>{selectedOperator?.municipio}</span>
                            <Button variant="secondary" size="sm" onClick={() => handleCloseMap(selectedOperator.matricula)}>
                                Cerrar
                            </Button>
                        </div>
                    </div>
                </Rnd>
            ))}

            {/* Modal: listado de OTs del operador */}
            <OperadorOTsModal
                show={otsModal.show}
                onHide={() => setOtsModal((prev) => ({ ...prev, show: false }))}
                matricula={otsModal.matricula}
                nombre={otsModal.nombre}
                onVerCasetas={(o) => handleOpenCasetas(o.ot, o.idTipoRuta, null, o.idClave, o.idUsuario)}
            />

            {/* Modal de Detalle del Directorio (Origen / Destino) */}
            <Modal show={showDirModal} onHide={() => setShowDirModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fas fa-building mr-2"></i>
                        {selectedDirEntry?.tipo}:{' '}
                        <span className="text-primary">{selectedDirEntry?.nombre || '—'}</span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedDirEntry && (
                        <dl className="row mb-0 small">
                            {selectedDirEntry.razon_social && (
                                <>
                                    <dt className="col-sm-4 text-muted">Razón Social</dt>
                                    <dd className="col-sm-8">{selectedDirEntry.razon_social}</dd>
                                </>
                            )}
                            {selectedDirEntry.direccion && (
                                <>
                                    <dt className="col-sm-4 text-muted">Dirección</dt>
                                    <dd className="col-sm-8">
                                        {selectedDirEntry.direccion}
                                        {selectedDirEntry.colonia ? `, Col. ${selectedDirEntry.colonia}` : ''}
                                    </dd>
                                </>
                            )}
                            {selectedDirEntry.contacto && (
                                <>
                                    <dt className="col-sm-4 text-muted">Contacto</dt>
                                    <dd className="col-sm-8">{selectedDirEntry.contacto}</dd>
                                </>
                            )}
                            {selectedDirEntry.celular && (
                                <>
                                    <dt className="col-sm-4 text-muted">Celular</dt>
                                    <dd className="col-sm-8">
                                        <a href={`tel:${selectedDirEntry.celular}`}>
                                            <i className="fas fa-phone mr-1"></i>{selectedDirEntry.celular}
                                        </a>
                                    </dd>
                                </>
                            )}
                            {selectedDirEntry.correo && (
                                <>
                                    <dt className="col-sm-4 text-muted">Correo</dt>
                                    <dd className="col-sm-8">
                                        <a href={`mailto:${selectedDirEntry.correo}`}>
                                            <i className="fas fa-envelope mr-1"></i>{selectedDirEntry.correo}
                                        </a>
                                    </dd>
                                </>
                            )}
                            {!selectedDirEntry.razon_social && !selectedDirEntry.direccion && !selectedDirEntry.contacto && !selectedDirEntry.celular && !selectedDirEntry.correo && (
                                <dd className="col-12 text-muted font-italic">Sin información de contacto en el directorio.</dd>
                            )}
                        </dl>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDirModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Perfil del Operador */}
            <Modal show={showOperadorModal} onHide={() => setShowOperadorModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="d-flex align-items-center">
                        <span className="bg-light text-secondary rounded-circle d-inline-flex align-items-center justify-content-center mr-3 flex-shrink-0" style={{ width: 56, height: 56 }}>
                            <i className="fas fa-user fa-lg"></i>
                        </span>
                        <div>
                            <div className="h4 mb-0 font-weight-bold text-primary">{operadorModal.perfil?.nombre || operadorModal.nombre || 'Operador'}</div>
                            <div className="small text-muted">
                                Matrícula: {operadorModal.matricula}
                                {operadorModal.perfil && (
                                    <span className={`badge badge-pill ml-2 badge-${operadorModal.perfil.activo ? 'success' : 'secondary'}`}>
                                        {operadorModal.perfil.activo ? 'Activo' : 'Baja'}
                                    </span>
                                )}
                            </div>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {operadorModal.loading && <p className="text-center text-muted mb-0">Cargando...</p>}
                    {operadorModal.error && <div className="alert alert-danger mb-0">{operadorModal.error}</div>}
                    {operadorModal.perfil && (() => {
                        const p = operadorModal.perfil;
                        const baseColor = { MTY: 'danger', SAH: 'success', OTRA: 'warning' }[p.base.codigo];
                        const a = p.antiguedad;
                        const partes = [
                            a.anios != null && a.anios > 0 ? `${a.anios} ${a.anios === 1 ? 'año' : 'años'}` : null,
                            a.meses != null && a.meses > 0 ? `${a.meses} ${a.meses === 1 ? 'mes' : 'meses'}` : null,
                        ].filter(Boolean);
                        const antiguedadTxt = a.fechaIngreso
                            ? (partes.length ? partes.join(' y ') : `${a.dias || 0} días`)
                            : 'Sin fecha de ingreso';
                        const fmtFecha = (f) => {
                            if (!f) return null;
                            const fecha = new Date(f);
                            return Number.isNaN(fecha.getTime())
                                ? null
                                : fecha.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' });
                        };
                        const fechaNacimiento = p.fechaNacimiento ? new Date(p.fechaNacimiento) : null;
                        let edad = null;
                        if (fechaNacimiento && !Number.isNaN(fechaNacimiento.getTime())) {
                            const hoy = new Date();
                            edad = hoy.getFullYear() - fechaNacimiento.getUTCFullYear();
                            if (
                                hoy.getMonth() < fechaNacimiento.getUTCMonth() ||
                                (hoy.getMonth() === fechaNacimiento.getUTCMonth() && hoy.getDate() < fechaNacimiento.getUTCDate())
                            ) {
                                edad--;
                            }
                            if (edad < 0) edad = null;
                        }

                        return (
                            <>
                                {!p.activo && (
                                    <div className="alert alert-secondary py-1 small">
                                        <i className="fas fa-user-slash mr-1"></i>Colaborador dado de baja el {fmtFecha(p.fechaBaja)}
                                    </div>
                                )}
                                <div className={`alert alert-${baseColor} d-flex align-items-center`}>
                                    <i className="fas fa-home fa-2x mr-3"></i>
                                    <div>
                                        <div className="small text-uppercase font-weight-bold">Base de regreso (según domicilio)</div>
                                        <div className="h5 mb-0">{p.base.nombre}</div>
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    {[
                                        { key: 'cel', label: 'Celular', valor: p.contacto.celular, icono: 'fas fa-phone', bg: 'bg-primary', href: p.contacto.celular && `tel:${p.contacto.celular}` },
                                        { key: 'tel', label: 'Teléfono', valor: p.contacto.telefono, icono: 'fas fa-phone-alt', bg: 'bg-info', href: p.contacto.telefono && `tel:${p.contacto.telefono}` },
                                        { key: 'curp', label: 'CURP', valor: p.curp, icono: 'fas fa-id-card', bg: 'bg-secondary' },
                                        { key: 'rfc', label: 'RFC', valor: p.rfc, icono: 'fas fa-file-invoice', bg: 'bg-secondary' },
                                    ].map((c) => (
                                        <div key={c.key} className="col-6 mb-2">
                                            <div className="border rounded p-2 d-flex align-items-center h-100">
                                                <span className={`${c.bg} text-white rounded-circle d-inline-flex align-items-center justify-content-center mr-2 flex-shrink-0`} style={{ width: 32, height: 32 }}>
                                                    <i className={c.icono}></i>
                                                </span>
                                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                    <div className="small text-muted">{c.label}</div>
                                                    <div className="font-weight-bold small text-truncate">
                                                        {c.valor ? (c.href ? <a href={c.href}>{c.valor}</a> : c.valor) : '—'}
                                                    </div>
                                                </div>
                                                {c.valor && (
                                                    <>
                                                        {c.key === 'cel' && (
                                                            <a className="btn btn-link btn-sm p-1 text-success" title="WhatsApp" href={`https://wa.me/${c.valor}`} target="_blank" rel="noopener noreferrer">
                                                                <i className="fab fa-whatsapp"></i>
                                                            </a>
                                                        )}
                                                        <button className="btn btn-link btn-sm p-1 text-secondary" title="Copiar" onClick={() => navigator.clipboard.writeText(c.valor)}>
                                                            <i className="far fa-copy"></i>
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="border rounded p-3 mb-3">
                                    <div className="row">
                                        <div className="col-md-7">
                                            <div className="font-weight-bold mb-2"><i className="far fa-user mr-2"></i>Datos personales</div>
                                            <dl className="row mb-0 small">
                                                <dt className="col-5 text-muted">Fecha de nacimiento</dt>
                                                <dd className="col-7">{fmtFecha(p.fechaNacimiento) || '—'}</dd>
                                                <dt className="col-5 text-muted">Edad</dt>
                                                <dd className="col-7">{edad != null ? `${edad} ${edad === 1 ? 'año' : 'años'}` : '—'}</dd>
                                                <dt className="col-5 text-muted">Domicilio</dt>
                                                <dd className="col-7 mb-0">
                                                    {p.domicilio.calle && <div>{p.domicilio.calle}</div>}
                                                    {p.domicilio.colonia && <div>Col. {p.domicilio.colonia}</div>}
                                                    <div>
                                                        {[p.domicilio.ciudad, p.domicilio.estado].filter(Boolean).join(', ')}
                                                        {p.domicilio.codigoPostal && ` — C.P. ${p.domicilio.codigoPostal}`}
                                                    </div>
                                                </dd>
                                            </dl>
                                        </div>
                                        <div className="col-md-5 border-left small">
                                            <div className="font-weight-bold mb-2"><i className="far fa-envelope mr-2"></i>Correo</div>
                                            {p.contacto.correo ? <a href={`mailto:${p.contacto.correo}`}>{p.contacto.correo}</a> : '—'}
                                            {p.domicilio.completo && (
                                                <div className="mt-2">
                                                    <a target="_blank" rel="noopener noreferrer"
                                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.domicilio.completo + ', México')}`}>
                                                        <i className="fas fa-map-marker-alt mr-1"></i>Ver en Google Maps <i className="fas fa-external-link-alt ml-1"></i>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="border rounded">
                                    <button type="button" className="btn btn-block text-left font-weight-bold bg-light d-flex justify-content-between align-items-center"
                                        onClick={() => setOperadorInfoAdicional((v) => !v)}>
                                        <span><i className="far fa-file-alt mr-2"></i>Información adicional</span>
                                        <i className={`fas fa-chevron-${operadorInfoAdicional ? 'up' : 'down'}`}></i>
                                    </button>
                                    {operadorInfoAdicional && (
                                        <div className="row p-3 small">
                                            <div className="col-6 mb-3">
                                                <div className="text-muted">Antigüedad</div>
                                                <div className="font-weight-bold">{antiguedadTxt}</div>
                                                {a.fechaIngreso && <div className="text-muted">Ingreso: {fmtFecha(a.fechaIngreso)}</div>}
                                                {a.contratoEventual && <span className="badge badge-light border">Contrato eventual</span>}
                                            </div>
                                            <div className="col-6 mb-3">
                                                <div className="text-muted">Estado laboral</div>
                                                <div className="font-weight-bold">{p.activo ? 'Activo' : 'Baja'}</div>
                                                <span className={`badge badge-${p.activo ? 'success' : 'secondary'}`}>{p.activo ? 'En operación' : 'Dado de baja'}</span>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-muted">Domicilio / Base</div>
                                                <div className="font-weight-bold">{p.base.nombre}</div>
                                            </div>
                                            <div className="col-6">
                                                <div className="text-muted">Área / Puesto</div>
                                                <div className="font-weight-bold">{p.puesto || '—'}</div>
                                                {(p.area || p.actividad) && <div className="text-muted">{p.area || p.actividad}</div>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-info" className="mr-auto" disabled={!operadorModal.perfil || operadorModal.loading}
                        onClick={handleOpenOperadorTagsModal}>
                        <i className="fas fa-tags mr-2"></i>Historial de TAGs
                    </Button>
                    <Button variant="primary" onClick={() => setShowOperadorModal(false)}>Cerrar</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={operadorTagsModal.show} onHide={() => setOperadorTagsModal((prev) => ({ ...prev, show: false }))} centered size="md">
                <Modal.Header closeButton>
                    <Modal.Title><i className="fas fa-tags text-info mr-2"></i>TAGs asignados</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="small text-muted mb-3">
                        Historial de dispositivos de {operadorModal.perfil?.nombre || operadorModal.nombre || 'este operador'}.
                    </div>
                    {operadorTagsModal.loading && <p className="text-center text-muted mb-0">Cargando historial...</p>}
                    {operadorTagsModal.error && <div className="alert alert-danger mb-0">{operadorTagsModal.error}</div>}
                    {!operadorTagsModal.loading && !operadorTagsModal.error && (
                        operadorTagsModal.tags.length ? (
                            <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0">
                                    <thead>
                                        <tr className="text-center">
                                            <th>Dispositivo TAG</th>
                                            <th>Asignación</th>
                                            <th>Baja</th>
                                            <th>Estado</th>
                                            <th>Ver registros</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {operadorTagsModal.tags.map((tag, index) => (
                                            <tr key={`${tag.tag}-${tag.fechaAsignacion || index}`}>
                                                <td className="font-weight-bold">{tag.tag}</td>
                                                <td>{formatDatabaseDate(tag.fechaAsignacion, { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                                <td>{formatDatabaseDate(tag.fechaBaja, { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                                <td>
                                                    <ToggleSwitch
                                                        size="sm"
                                                        checked={!!tag.activo}
                                                        label={tag.activo ? 'Desactivar TAG' : 'Reactivar TAG'}
                                                        title={tag.activo ? 'Activo: clic para desactivar' : 'Inactivo: clic para reactivar'}
                                                        onChange={() => handleToggleTagClick(tag)}
                                                    />
                                                </td>
                                                <td className="d-flex gap-2">
                                                    <a href={`https://apps.pase.com.mx/uc/detalletag/IMDM/${tag?.tag?.replace("IMDM", "")}`} style={{ fontSize: '.9rem', }} target='_blank' rel="noreferrer"  className="btn fs-6 btn-sm btn-success d-flex align-items-center mr-2">
                                                        PASE
                                                    </a>
                                                    <button className="btn fs-6 btn-sm btn-primary d-flex align-items-center" onClick={() => handleOpenTagModal(tag?.tag)}>
                                                        TUSA
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center text-muted py-3">
                                <i className="fas fa-tag fa-2x mb-2"></i>
                                <div>No hay TAGs registrados para este operador.</div>
                            </div>
                        )
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setOperadorTagsModal((prev) => ({ ...prev, show: false }))}>Cerrar</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!tagToggleConfirm} onHide={cancelTagToggle} centered style={{ zIndex: 1200 }}>
                <Modal.Header closeButton>
                    <Modal.Title>{tagToggleConfirm?.accion === 'REACTIVAR' ? 'Confirmar Reactivación' : 'Confirmar Desactivación'}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <p className="mb-0">
                        ¿Se {tagToggleConfirm?.accion === 'REACTIVAR' ? 'reactivó' : 'desactivó'} el TAG <strong>{tagToggleConfirm?.tag}</strong> en el portal del proveedor?
                    </p>
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button variant="secondary" onClick={cancelTagToggle} disabled={tagToggleConfirm?.saving}>Aún no</Button>
                    <Button variant="primary" onClick={confirmTagToggle} disabled={tagToggleConfirm?.saving}>
                        {tagToggleConfirm?.saving ? 'Actualizando...' : 'Sí, actualizar en BD'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Panel flotante de registros del TAG en TUSA */}
            {showTagModal && (
                <Rnd
                    default={{
                        x: Math.max(0, (window.innerWidth - 700) / 2),
                        y: Math.max(0, (window.innerHeight - 600) / 2),
                        width: 700,
                        height: 600,
                    }}
                    minWidth={420}
                    minHeight={300}
                    dragHandleClassName="tag-panel-handle"
                    style={{ zIndex: 1150, position: 'fixed' }}
                    enableResizing={{ top: true, right: true, bottom: true, left: true, topRight: true, bottomRight: true, bottomLeft: true, topLeft: true }}
                >
                    <div className="card shadow-lg h-100 d-flex flex-column" style={{ border: '2px solid #17a2b8', borderRadius: '0.5rem', overflow: 'hidden', backgroundColor: '#fff' }}>

                        {/* Cabecera draggable */}
                        <div
                            className="card-header bg-info d-flex align-items-center justify-content-between tag-panel-handle"
                            style={{ cursor: 'move', userSelect: 'none', padding: '0.6rem 1rem' }}
                        >
                            <h6 className="m-0 font-weight-bold text-white">
                                <i className="fas fa-tag mr-2"></i>
                                Registros del TAG: <span className="font-weight-normal">{tagModalData.tag}</span>
                                <small className="ml-2 font-weight-normal" style={{ fontSize: '0.75rem', opacity: 0.85 }}>(últimos 90 días)</small>
                            </h6>
                            <button type="button" className="close text-white" style={{ opacity: 1 }} onClick={() => setShowTagModal(false)}>
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>

                        {/* Cuerpo */}
                        <div className="card-body p-3 d-flex flex-column" style={{ overflow: 'auto', flex: 1 }} onMouseDown={e => e.stopPropagation()}>
                            {tagModalData.loading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-info" role="status"><span className="sr-only">Cargando...</span></div>
                                    <p className="mt-2 text-muted">Consultando registros...</p>
                                </div>
                            ) : tagModalData.error ? (
                                <div className="alert alert-danger">{tagModalData.error}</div>
                            ) : tagModalData.cruces.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="fas fa-inbox fa-2x mb-2"></i>
                                    <p>Sin registros en los últimos 90 días.</p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-muted small mb-2"><strong>{tagModalData.cruces.length}</strong> registros encontrados.</p>
                                    <div className="table-responsive" style={{ flex: 1 }}>
                                        <table className="table table-sm table-bordered table-striped" style={{ fontSize: '0.8rem' }}>
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>Fecha</th>
                                                    <th>Caseta</th>
                                                    <th>Clase</th>
                                                    <th>OT</th>
                                                    <th>Estatus</th>
                                                    <th className="text-right">Importe</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tagModalData.cruces.map(c => (
                                                    <tr key={c.ID}>
                                                        <td className="text-nowrap">{formatDatabaseDate(c.Fecha, { dateStyle: 'short', timeStyle: 'short' })}</td>
                                                        <td>{c.Caseta}</td>
                                                        <td className="text-muted">{c.Clase || '-'}</td>
                                                        <td>
                                                            {c.id_orden && tagOTInputs[c.ID] === undefined ? (
                                                                <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                                                                    <span className="text-muted">{c.id_orden}</span>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-secondary py-0 px-1"
                                                                        onClick={() => setTagOTInputs(prev => ({ ...prev, [c.ID]: c.id_orden }))}
                                                                        title="Corregir OT asignada"
                                                                        disabled={tagSavingOT.has(c.ID)}
                                                                    >
                                                                        <i className="fas fa-edit"></i>
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="d-flex align-items-center" style={{ gap: '4px', minWidth: '180px' }}>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control form-control-sm"
                                                                        placeholder="OT-XXXXX"
                                                                        value={tagOTInputs[c.ID] || ''}
                                                                        onChange={e => setTagOTInputs(prev => ({ ...prev, [c.ID]: e.target.value }))}
                                                                        onKeyDown={e => e.key === 'Enter' && handleAssignOTtoTagCruce(c.ID, tagOTInputs[c.ID])}
                                                                        disabled={tagSavingOT.has(c.ID)}
                                                                        style={{ width: '110px', flexShrink: 0 }}
                                                                    />
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => handleAssignOTtoTagCruce(c.ID, tagOTInputs[c.ID])}
                                                                        disabled={!tagOTInputs[c.ID] || tagSavingOT.has(c.ID)}
                                                                        title="Guardar OT"
                                                                    >
                                                                        {tagSavingOT.has(c.ID)
                                                                            ? <i className="fas fa-spinner fa-spin"></i>
                                                                            : <i className="fas fa-link"></i>}
                                                                    </button>
                                                                    {c.id_orden && (
                                                                        <button
                                                                            className="btn btn-sm btn-outline-secondary"
                                                                            onClick={() => setTagOTInputs(prev => { const n = { ...prev }; delete n[c.ID]; return n; })}
                                                                            title="Cancelar edición"
                                                                            disabled={tagSavingOT.has(c.ID)}
                                                                        >
                                                                            <i className="fas fa-times"></i>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${c.Estatus === 'Confirmado' ? 'badge-success' :
                                                                c.Estatus === 'Abuso' ? 'badge-danger' :
                                                                    c.Estatus === 'Aclaración' ? 'badge-warning' :
                                                                        'badge-secondary'
                                                                } text-wrap`}>{c.Estatus}</span>
                                                        </td>
                                                        <td className="text-right font-weight-bold">${parseFloat(c.Importe || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="thead-light">
                                                <tr>
                                                    <td colSpan="5" className="text-right font-weight-bold">Total:</td>
                                                    <td className="text-right font-weight-bold text-primary">${tagModalData.cruces.reduce((sum, c) => sum + parseFloat(c.Importe || 0), 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Pie */}
                        <div className="card-footer py-2 d-flex justify-content-end bg-light">
                            <button className="btn btn-secondary btn-sm" onClick={() => setShowTagModal(false)}>Cerrar</button>
                        </div>
                    </div>
                </Rnd>
            )}

            {/* Modal de Casetas */}
            <Modal show={showCasetasModal} onHide={() => { setShowCasetasModal(false); setManualLinks({}); setSearchParams(prev => { const next = new URLSearchParams(prev); next.delete('ot'); return next; }); }} size="lg" centered enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fas fa-road mr-2"></i>
                        Detalle de Casetas - OT: <span className="text-primary">{casetasData.ot} </span>
                        {casetasData.id_tipo_ruta && (
                            <span
                                className="badge badge-dark ml-2"
                                style={{ fontSize: '1rem', cursor: 'pointer' }}
                                onClick={() => navigate(`/rutas?ruta=${casetasData.id_tipo_ruta}`)}
                                title="Ver ruta en visor"
                            >
                                Ruta: {casetasData.id_tipo_ruta}
                            </span>
                        )}
                        {casetasData.id_clave && <span className="badge badge-info ml-2" style={{ fontSize: '1rem' }}>{casetasData.id_clave}</span>}
                        {casetasData.id_usuario && (
                            <span className="badge badge-secondary ml-2" style={{ fontSize: '1rem' }}>
                                Tráfico: {casetasData.id_usuario}
                            </span>
                        )}
                        {(() => {
                            const tagVal = casetasData.tag || casetasData.crossed?.find(c => c.Tag)?.Tag;
                            return tagVal ? (
                                <a
                                    href={`https://apps.pase.com.mx/uc/detalletag/IMDM/${tagVal.replace("IMDM", "")}`}
                                    className="btn btn-sm btn-outline-primary ml-3"
                                    style={{ fontSize: '.9rem' }}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="Ver TAG en PASE"
                                >
                                    <i className="fas fa-tag mr-1"></i> TAG: {tagVal}
                                </a>
                            ) : null;
                        })()}
                        {casetasData.id_tipo_ruta && (
                            <button
                                className="btn btn-sm btn-outline-warning ml-2"
                                style={{ fontSize: '.9rem' }}
                                onClick={handleOpenEditRuta}
                                title="Editar casetas de la ruta"
                            >
                                <i className="fas fa-edit mr-1"></i> Editar ruta
                            </button>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {casetasData.otDetails && casetasData.otDetails.info && (
                        <div className="card shadow-sm mb-3">
                            <div className="card-body p-3">
                                <h6 className="card-title text-primary"><i className="fas fa-info-circle mr-2"></i>Detalles Rápidos de la Orden</h6>
                                <div className="row small">
                                    <div className="col-12 col-md-6">
                                        <p className="mb-1"><strong>Operador:</strong> {casetasData.otDetails.info.OperadorNombre} (Matricula: {casetasData.otDetails.info.ID_matricula})</p>
                                        <p className="mb-1"><strong>VIN:</strong> <span className={casetasData.otDetails.info.No_chasis ? "text-info" : "text-muted"}>{casetasData.otDetails.info.No_chasis || 'N/A'}</span></p>
                                        <p className="mb-1"><strong>Asignada:</strong> <span className={casetasData.otDetails.info.asignada ? "text-success" : "text-muted"}>{casetasData.otDetails.info.asignada ? new Date(casetasData.otDetails.info.asignada).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }) : 'Pendiente'}</span></p>
                                        <p className="mb-1"><strong>En Origen:</strong> <span className={casetasData.otDetails.info.origen_status ? "text-success" : "text-muted"}>{casetasData.otDetails.info.origen_status ? new Date(casetasData.otDetails.info.origen_status).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }) : 'Pendiente'}</span></p>

                                        {(() => {
                                            const tagVal = casetasData.tag || casetasData.crossed?.find(c => c.Tag)?.Tag;
                                            return <p className='mb-1'><strong>TAG:</strong> <span className={tagVal ? "text-info" : "text-muted"}>{tagVal ? <button className="btn btn-link p-0 text-info" style={{ fontSize: 'inherit', verticalAlign: 'baseline' }} onClick={() => handleOpenTagModal(tagVal)} title="Ver registros del TAG en TUSA">{tagVal}</button> : 'N/A'}</span></p>;
                                        })()}
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <p className="mb-1"><strong>Iniciada:</strong> <span className={casetasData.otDetails.info.iniciada ? "text-success" : "text-muted"}>{casetasData.otDetails.info.iniciada ? new Date(casetasData.otDetails.info.iniciada).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }) : 'Pendiente'}</span></p>
                                        <p className="mb-1"><strong>En Destino:</strong> <span className={casetasData.otDetails.info.en_sitio ? "text-success" : "text-muted"}>{casetasData.otDetails.info.en_sitio ? new Date(casetasData.otDetails.info.en_sitio).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }) : 'Pendiente'}</span></p>
                                        <p className="mb-1"><strong>Finalizada:</strong> <span className={casetasData.otDetails.info.finalizada ? "text-success" : "text-muted"}>{casetasData.otDetails.info.finalizada ? new Date(casetasData.otDetails.info.finalizada).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }) : 'Pendiente'}</span></p>
                                    </div>
                                </div>
                                {casetasData.otDetails.tracking && casetasData.otDetails.tracking.length > 0 && (
                                    <div className="mt-2 d-flex justify-content-end">
                                        <button className="btn btn-outline-info b¿tn-sm" onClick={() => {
                                            const pts = casetasData.otDetails.tracking.filter(t => t.latitud && t.longitud);
                                            if (pts.length > 0) {
                                                const lastPt = pts[pts.length - 1];
                                                const newOp = {
                                                    matricula: casetasData.otDetails.info.ID_matricula,
                                                    nombre: casetasData.otDetails.info.OperadorNombre,
                                                    latitud: lastPt.latitud,
                                                    longitud: lastPt.longitud,
                                                    fecha_gps: lastPt.fecha,
                                                    municipio: 'Última ubicación de ruta resguardada',
                                                    rutas: pts
                                                };
                                                setOpenMapWindows(prev => {
                                                    const isAlreadyOpen = prev.some(op => op.matricula === newOp.matricula);
                                                    if (isAlreadyOpen) {
                                                        const filtered = prev.filter(op => op.matricula !== newOp.matricula);
                                                        return [...filtered, newOp]; // overwrites with trace mode
                                                    }
                                                    return [...prev, newOp];
                                                });
                                            } else {
                                                alert("No hay ubicaciones registradas con coordenadas válidas para esta OT.");
                                            }
                                        }}>
                                            <i className="fas fa-map-marked-alt mr-2"></i>Ver recorrido en el mapa ({casetasData.otDetails.tracking.length} puntos)
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {casetasData.loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="sr-only">Cargando...</span>
                            </div>
                            <p className="mt-2 text-muted">Obteniendo cruces y casetas esperadas...</p>
                        </div>
                    ) : casetasData.error ? (
                        <div className="alert alert-danger">{casetasData.error}</div>
                    ) : (
                        <div className="table-responsive table-container">
                            <table className="table table-sm table-bordered table-small table-striped table-scroll" >
                                <thead className="thead-light">
                                    <tr>
                                        <th>Orden</th>
                                        <th>Caseta Esperada</th>
                                        <th>Estado</th>
                                        <th>Detalles del Cruce</th>
                                        <th className="text-center">Consec. propuesto</th>
                                        <th>Costo estimado</th>
                                        <th>Costo del cruce</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const sortedCrossings = [...casetasData.crossed].sort((a, b) => new Date(a.Fecha) - new Date(b.Fecha));
                                        // Normaliza acentos/diacríticos para comparación tolerante
                                        const normStr = str => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
                                        return casetasData.expected.length === 0 ? (
                                            <tr><td colSpan="7" className="text-center text-muted py-3">No hay casetas registradas para esta ruta.</td></tr>
                                        ) : (
                                            casetasData.expected.map((caseta, i) => {
                                                // Buscar si hay un cruce que concuerde.
                                                const cruce = casetasData.crossed.find(c => {
                                                    const manualMatch = manualLinks[c.ID] === caseta.ID_Caseta;

                                                    return manualMatch ||
                                                        (c.idCaseta).toString() === (caseta.ID_Caseta).toString() ||
                                                        (c.Caseta && caseta.Nombre_IAVE && normStr(c.Caseta).includes(normStr(caseta.Nombre_IAVE))) ||
                                                        (c.Caseta && caseta.Nombre && normStr(c.Caseta).includes(normStr(caseta.Nombre)));
                                                });

                                                // Gris claro/blanco = Pendiente. Verde claro (table-success) = Cruzada.
                                                return (
                                                    <tr key={i} className={cruce ? 'table-success' : 'text-muted'}>
                                                        <td className="text-center align-middle"><strong>{i + 1}</strong></td>
                                                        <td className="align-middle">
                                                            <strong>{caseta.Nombre_IAVE || caseta.Nombre || caseta.Caseta}</strong>
                                                            <div className="small font-italic">{caseta.Notas || 'N/A'}</div>
                                                        </td>
                                                        <td className="text-center align-middle">
                                                            {cruce ? (
                                                                <span className="badge badge-success"><i className="fas fa-check"></i> Cruzada</span>
                                                            ) : (
                                                                <span className="badge badge-secondary"><i className="fas fa-clock"></i> Pendiente</span>
                                                            )}
                                                        </td>
                                                        <td className="align-middle">
                                                            {cruce ? (
                                                                <div className="small">
                                                                    <div><strong>Fecha:</strong> {formatDatabaseDate(cruce.Fecha)}</div>
                                                                    {cruce.Tag && <div><strong>TAG:</strong> {cruce.Tag}</div>}
                                                                </div>
                                                            ) : (
                                                                <span className="small font-italic">-</span>
                                                            )}
                                                        </td>
                                                        <td className="text-center align-middle">
                                                            {cruce ? (
                                                                <span className="font-weight-bold text-info" title="Posición cronológica del cruce entre todos los registros">
                                                                    {sortedCrossings.findIndex(x => x.ID === cruce.ID) + 1}
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>

                                                        <td className="align-middle">
                                                            {caseta[claveToImporteField[casetasData.id_clave?.trim()]] !== undefined ? (
                                                                <div className="small">
                                                                    <div>$<strong>{caseta[claveToImporteField[casetasData.id_clave?.trim()]]?.toLocaleString('es-MX')}</strong></div>
                                                                </div>
                                                            ) : (
                                                                <span className="small font-italic">-</span>
                                                            )}
                                                        </td>
                                                        {/* Columna de costo del cruce real.*/}
                                                        <td className="align-middle">
                                                            {cruce ? (
                                                                <div className="small">
                                                                    <div>$<strong>{cruce?.Importe?.toLocaleString('es-MX')}</strong></div>
                                                                </div>
                                                            ) : (
                                                                <span className="small font-italic">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        );
                                    })()}
                                </tbody>
                            </table>
                            {(() => {
                                const sortedCrossingsAll = [...casetasData.crossed].sort((a, b) => new Date(a.Fecha) - new Date(b.Fecha));
                                const otIniciada = casetasData.otDetails?.info?.iniciada ? new Date(casetasData.otDetails.info.iniciada) : null;
                                const otFin = casetasData.otDetails?.info?.en_sitio
                                    ? new Date(casetasData.otDetails.info.en_sitio)
                                    : (casetasData.otDetails?.info?.finalizada ? new Date(casetasData.otDetails.info.finalizada) : null);
                                const noMapeados = casetasData.crossed.filter(c => {
                                    if (manualLinks[c.ID]) return false;
                                    // Excluir cruces cuya fecha esté fuera del rango de la OT
                                    if (otIniciada || otFin) {
                                        const cruceFecha = new Date(c.Fecha);
                                        if (otIniciada && cruceFecha < otIniciada) return false;
                                        if (otFin && cruceFecha > otFin) return false;
                                    }
                                    return !casetasData.expected.some(e =>
                                        (e.ID_Caseta?.toString()) === (c.idCaseta?.toString()) ||
                                        (e.Nombre_IAVE && c.Caseta.toUpperCase().includes(e.Nombre_IAVE.toUpperCase())) ||
                                        (e.Nombre && c.Caseta.toUpperCase().includes(e.Nombre.toUpperCase()))
                                    );
                                });

                                return noMapeados.length > 0 && (
                                    <div className="mt-4">
                                        <h6 className="text-danger"><i className="fas fa-exclamation-triangle mr-1"></i> Cruces registrados no mapeados a la ruta:</h6>
                                        <table className="table table-sm table-bordered mt-2">
                                            <thead className="table-danger">
                                                <tr>
                                                    <th>Caseta Reportada</th>
                                                    <th>Fecha</th>
                                                    <th className="text-center">Consec. propuesto</th>
                                                    <th>Importe</th>
                                                    <th>Vincular a Caseta Esperada</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {noMapeados.map((cruce, i) => {
                                                    const unfulfilledExpected = casetasData.expected.filter(e => {
                                                        return !casetasData.crossed.some(c2 => {
                                                            return manualLinks[c2.ID] === e.ID_Caseta ||
                                                                (c2.idCaseta?.toString()) === (e.ID_Caseta?.toString()) ||
                                                                (c2.Caseta && e.Nombre_IAVE && c2.Caseta.toUpperCase().includes(e.Nombre_IAVE.toUpperCase())) ||
                                                                (c2.Caseta && e.Nombre && c2.Caseta.toUpperCase().includes(e.Nombre.toUpperCase()));
                                                        });
                                                    });

                                                    return (
                                                        <tr key={i}>
                                                            <td className="align-middle"><strong>{cruce.Caseta}</strong></td>
                                                            <td className="align-middle">{formatDatabaseDate(cruce.Fecha)}</td>
                                                            <td className="text-center align-middle">
                                                                <span className="font-weight-bold text-info" title="Posición cronológica entre todos los cruces registrados">
                                                                    {sortedCrossingsAll.findIndex(x => x.ID === cruce.ID) + 1}
                                                                </span>
                                                            </td>
                                                            <td className="align-middle">${cruce.Importe}</td>
                                                            <td className="align-middle">
                                                                <select
                                                                    className="form-control form-control-sm"
                                                                    value=""
                                                                    onChange={(e) => handleLinkCruce(cruce.ID, parseInt(e.target.value))}
                                                                >
                                                                    <option value="" disabled>Seleccione para vincular...</option>
                                                                    {unfulfilledExpected.map(pend => (
                                                                        <option key={pend.ID_Caseta} value={pend.ID_Caseta}>
                                                                            {pend.Nombre_IAVE || pend.Nombre || pend.Caseta}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}

                            {/* ── Opción 2: Cruces del TAG fuera del rango de la OT ── */}
                            {(() => {
                                const tagCruces = casetasData.tagCruces || [];
                                if (!tagCruces.length) return null;

                                const otIniciada = casetasData.otDetails?.info?.iniciada ? new Date(casetasData.otDetails.info.iniciada) : null;
                                const otFin = casetasData.otDetails?.info?.en_sitio
                                    ? new Date(casetasData.otDetails.info.en_sitio)
                                    : (casetasData.otDetails?.info?.finalizada ? new Date(casetasData.otDetails.info.finalizada) : null);

                                // Cruces del TAG: sin OT asignada + fuera del rango de la OT + no ya presentes en crossed
                                const outOfRange = tagCruces.filter(tc => {
                                    if (tc.id_orden) return false;
                                    const tcFecha = new Date(tc.Fecha);
                                    const outsideRange = (otFin && tcFecha > otFin) || (otIniciada && tcFecha < otIniciada);
                                    if (!outsideRange) return false;
                                    return !casetasData.crossed.some(c => c.ID === tc.ID);
                                });

                                if (!outOfRange.length) return null;

                                return (
                                    <div className="mt-4">
                                        <h6 className="text-warning">
                                            <i className="fas fa-exclamation-circle mr-1"></i> Cruces del TAG sin OT fuera del rango de la orden:
                                        </h6>
                                        <p className="small text-muted mb-2">
                                            Estos cruces pertenecen al mismo TAG pero ocurrieron fuera del período registrado de la OT
                                            (posiblemente porque el operador la cerró anticipadamente). Puedes vincularlos a una caseta pendiente
                                            para asignarles la OT <strong>{casetasData.ot}</strong> y conciliar el costo.
                                        </p>
                                        <table className="table table-sm table-bordered mt-1" style={{ backgroundColor: '#fff9e6' }}>
                                            <thead className="table-warning">
                                                <tr>
                                                    <th>Caseta Reportada</th>
                                                    <th>Fecha</th>
                                                    <th className="text-center">Importe</th>
                                                    <th>Vincular a Caseta Esperada (asignará la OT)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {outOfRange.map((tc, i) => {
                                                    // Casetas pendientes aún no cubiertas (ni por crossed ni por otros outOfRange ya vinculados)
                                                    const unfulfilledExpected = casetasData.expected.filter(e => {
                                                        const coveredByCrossed = casetasData.crossed.some(c2 =>
                                                            manualLinks[c2.ID] === e.ID_Caseta ||
                                                            (c2.idCaseta?.toString()) === (e.ID_Caseta?.toString()) ||
                                                            (c2.Caseta && e.Nombre_IAVE && c2.Caseta.toUpperCase().includes(e.Nombre_IAVE.toUpperCase())) ||
                                                            (c2.Caseta && e.Nombre && c2.Caseta.toUpperCase().includes(e.Nombre.toUpperCase()))
                                                        );
                                                        const coveredByOutOfRange = outOfRange.some((oc, oi) => oi < i && manualLinks[oc.ID] === e.ID_Caseta);
                                                        return !coveredByCrossed && !coveredByOutOfRange;
                                                    });

                                                    return (
                                                        <tr key={tc.ID} className={manualLinks[tc.ID] ? 'table-success' : ''}>
                                                            <td className="align-middle"><strong>{tc.Caseta}</strong></td>
                                                            <td className="align-middle text-nowrap">
                                                                {formatDatabaseDate(tc.Fecha)}
                                                            </td>
                                                            <td className="text-center align-middle font-weight-bold">
                                                                ${parseFloat(tc.Importe || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="align-middle">
                                                                {manualLinks[tc.ID] ? (
                                                                    <div className="d-flex align-items-center">
                                                                        <span className="badge badge-success mr-2">
                                                                            <i className="fas fa-check mr-1"></i>
                                                                            {casetasData.expected.find(e => e.ID_Caseta === manualLinks[tc.ID])?.Nombre_IAVE || 'Vinculado'}
                                                                        </span>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-secondary"
                                                                            onClick={() => handleLinkCruce(tc.ID, null)}
                                                                            title="Quitar vínculo"
                                                                        >
                                                                            <i className="fas fa-times"></i>
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <select
                                                                        className="form-control form-control-sm"
                                                                        value=""
                                                                        onChange={(e) => handleLinkCruce(tc.ID, parseInt(e.target.value))}
                                                                    >
                                                                        <option value="" disabled>Vincular y asignar a {casetasData.ot}...</option>
                                                                        {unfulfilledExpected.map(pend => (
                                                                            <option key={pend.ID_Caseta} value={pend.ID_Caseta}>
                                                                                {pend.Nombre_IAVE || pend.Nombre || pend.Caseta}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {Object.keys(manualLinks).length > 0 && (
                        <div className="mr-auto">
                            <span className="text-warning small"><i className="fas fa-info-circle mr-1"></i>Tienes {Object.keys(manualLinks).length} cambio(s) sin guardar</span>
                        </div>
                    )}
                    <Button variant="secondary" onClick={() => { setShowCasetasModal(false); setManualLinks({}); setSearchParams(prev => { const next = new URLSearchParams(prev); next.delete('ot'); return next; }); }} disabled={savingLinks}>
                        Cerrar
                    </Button>
                    {Object.keys(manualLinks).length > 0 && (
                        <Button variant="primary" onClick={handleSaveLinks} disabled={savingLinks}>
                            {savingLinks ? (
                                <span><i className="fas fa-spinner fa-spin mr-2"></i>Guardando...</span>
                            ) : (
                                <span><i className="fas fa-save mr-2"></i>Guardar Conciliación</span>
                            )}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Panel flotante: Editar casetas de la ruta */}
            {showEditRutaPanel && (
                <EditRutaPanel
                    id_tipo_ruta={casetasData.id_tipo_ruta}
                    ot={casetasData.ot}
                    initialCasetas={casetasData.expected}
                    onClose={() => setShowEditRutaPanel(false)}
                    onSaved={() => {
                        setShowEditRutaPanel(false);
                        handleOpenCasetas(casetasData.ot, casetasData.id_tipo_ruta, casetasData.tag, casetasData.id_clave, casetasData.id_usuario);
                    }}
                />
            )}
        </div>
    );
};

export default TrackingModule;